
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../lib/http/axiosConfig";
import { APIProvider, Map, Marker, useMap } from "@vis.gl/react-google-maps";
import CalmSelect from "./CalmSelect";
import "../../Styles/AdminUsersPage.css";

const EMPTY_DRAFT = { nickname: "", email: "", role: "USER" };
const EMPTY_USER_FORM = {
  username: "",
  password: "",
  nickname: "",
  email: "",
  phone: "",
  role: "USER",
};
const EMPTY_FARM_FORM = {
  name: "",
  address: "",
  cropId: "",
};
const EMPTY_CROP_FORM = {
  nameKo: "",
  nameEn: "",
};
const EMPTY_DRONE_FORM = {
  ownerId: "",
  farmId: "",
  droneName: "",
  serialNum: "",
};
const EMPTY_SMART_POLE_FORM = {
  userId: "",
  name: "",
  serialNum: "",
  modelName: "",
  latitude: "",
  longitude: "",
  locationName: "",
  deviceType: "POLE",
};

// 스마트폴과 태양광큐브는 smart_pole 한 테이블에 device_type 으로만 구분돼 있다.
// 농장에는 폴 1대 "또는" 큐브 여러 대(fog 0~1대 + edge)가 붙는다(V11). 폴·큐브 공존 금지와
// fog 규칙은 BE(FarmService.reconcileEnergyDevices)가 검사하고, 여기서는 저장 전 미리보기로 같은 규칙을 보여준다.
// 장치를 농장에 넣고 빼는 자리는 사용자 관리 › 수정 › 장비 연결 한 곳뿐이다 — 큐브 수정 모달은 농장을 바꾸지 않는다.
const DEVICE_TYPES = ["POLE", "CUBE"];
const DEVICE_LABEL = { POLE: "스마트폴", CUBE: "태양광큐브" };
const normalizeDeviceType = (value) =>
  String(value ?? "POLE").toUpperCase() === "CUBE" ? "CUBE" : "POLE";

// 농장 목록 "연결 장치" 열. BE 가 farm 마다 드론 1대 + 에너지 장치 요약(종류·대수·fog 여부)을 내려준다.
const formatFarmDevices = (farm) => {
  const parts = [];
  if (farm?.droneId != null) parts.push("드론 1");
  const count = Number(farm?.energyDeviceCount) || 0;
  if (count > 0) {
    if (normalizeDeviceType(farm.energyDeviceType) === "POLE") parts.push("스마트폴 1");
    else parts.push(`큐브 ${count} (${farm.hasFog ? "fog 1" : "fog 없음"})`);
  }
  return parts.length ? parts.join(" · ") : "없음";
};

const DEFAULT_CENTER = { lat: 35.0, lng: 126.7 };

const MapPanTo = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !center) return;
    map.panTo(center);
    map.setZoom(16);
  }, [map, center]);
  return null;
};

const MapPolygon = ({ points }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !window.google?.maps || points.length < 2) return;

    const polygon = new window.google.maps.Polygon({
      paths: points.map((point) => ({ lat: point[0], lng: point[1] })),
      strokeColor: "#4da3ff",
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: "#4da3ff",
      fillOpacity: 0.2,
      clickable: false,
      map,
    });

    return () => {
      polygon.setMap(null);
    };
  }, [map, points]);

  return null;
};

const AdminTest = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [crops, setCrops] = useState([]);
  const [drones, setDrones] = useState([]);
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cropLoading, setCropLoading] = useState(false);
  const [droneLoading, setDroneLoading] = useState(false);
  const [smartPoleLoading, setSmartPoleLoading] = useState(false);
  const [error, setError] = useState("");
  const [cropError, setCropError] = useState("");
  const [droneError, setDroneError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [modalError, setModalError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userForm, setUserForm] = useState(EMPTY_USER_FORM);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [farmForm, setFarmForm] = useState(EMPTY_FARM_FORM);
  const [boundaryPoints, setBoundaryPoints] = useState([]);
  const [mapSearchQuery, setMapSearchQuery] = useState("");
  const [mapCenter, setMapCenter] = useState(null);
  const [createdUser, setCreatedUser] = useState(null);
  const [createdFarmId, setCreatedFarmId] = useState(null);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  const [sortKey, setSortKey] = useState("id");
  const [sortDir, setSortDir] = useState("asc");
  const [cropSortKey, setCropSortKey] = useState("cropId");
  const [cropSortDir, setCropSortDir] = useState("asc");
  const [droneSortKey, setDroneSortKey] = useState("id");
  const [droneSortDir, setDroneSortDir] = useState("asc");
  const [smartPoleSortKey, setSmartPoleSortKey] = useState("serialNum");
  const [smartPoleSortDir, setSmartPoleSortDir] = useState("asc");
  const [farmSortKey, setFarmSortKey] = useState("id");
  const [farmSortDir, setFarmSortDir] = useState("asc");
  const [farmLoading, setFarmLoading] = useState(false);
  const [farmError, setFarmError] = useState("");
  const [farmQuery, setFarmQuery] = useState("");
  const [userColWidths, setUserColWidths] = useState([
    70,
    140,
    150,
    220,
    100,
    120,
     170,
     120,
   ]);
  const [cropColWidths, setCropColWidths] = useState([
    70,
    180,
    180,
    120,
    160,
  ]);
  const [droneColWidths, setDroneColWidths] = useState([
    90,
    140,
    180,
    170,
    130,
    160,
  ]);
  // ID · 이름 · 시리얼 · 닉네임 · 농장 · 역할(큐브 탭만) · 상태 · 작업
  const [smartPoleColWidths, setSmartPoleColWidths] = useState([
    60,
    140,
    170,
    150,
    160,
    90,
    100,
    140,
  ]);
  // ID · 농장명 · 주소 · 닉네임 · 작물 · 면적 · 연결 장치 · 작업
  const [farmColWidths, setFarmColWidths] = useState([
    60,
    120,
    180,
    150,
    100,
    90,
    170,
    140,
  ]);
  const [resizeState, setResizeState] = useState(null);

  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [cropForm, setCropForm] = useState(EMPTY_CROP_FORM);
  const [cropModalError, setCropModalError] = useState("");
  const [cropEditingId, setCropEditingId] = useState(null);
  const [cropModalFromEditFlow, setCropModalFromEditFlow] = useState(false);
  const [smartPoles, setSmartPoles] = useState([]);
  const [isSmartPoleModalOpen, setIsSmartPoleModalOpen] = useState(false);
  const [smartPoleForm, setSmartPoleForm] = useState(EMPTY_SMART_POLE_FORM);
  const [smartPoleModalError, setSmartPoleModalError] = useState("");
  const [smartPoleModalLockUser, setSmartPoleModalLockUser] = useState(false);
  const [isSmartPoleDroneModalOpen, setIsSmartPoleDroneModalOpen] =
    useState(false);
  const [smartPoleDroneTargetId, setSmartPoleDroneTargetId] = useState(null);
  const [smartPoleDroneId, setSmartPoleDroneId] = useState("");
  const [smartPoleDroneModalError, setSmartPoleDroneModalError] = useState("");
  const [isSmartPoleEditModalOpen, setIsSmartPoleEditModalOpen] = useState(false);
  const [smartPoleEditTargetId, setSmartPoleEditTargetId] = useState(null);
  const [smartPoleEditForm, setSmartPoleEditForm] = useState({ name: "", serialNum: "", status: "" });
  const [smartPoleEditModalError, setSmartPoleEditModalError] = useState("");

  const [isFarmModalOpen, setIsFarmModalOpen] = useState(false);
  const [farmModalForm, setFarmModalForm] = useState({ name: "", address: "", cropId: "", ownerId: "" });
  const [farmModalError, setFarmModalError] = useState("");
  const [farmModalBoundary, setFarmModalBoundary] = useState([]);
  const [farmModalMapSearch, setFarmModalMapSearch] = useState("");
  const [farmModalMapCenter, setFarmModalMapCenter] = useState(null);
  const [farmModalSubmitting, setFarmModalSubmitting] = useState(false);

  const [isFarmEditModalOpen, setIsFarmEditModalOpen] = useState(false);
  const [farmEditTargetId, setFarmEditTargetId] = useState(null);
  const [farmEditForm, setFarmEditForm] = useState({ name: "", address: "", cropId: "", ownerId: "" });
  const [farmEditError, setFarmEditError] = useState("");
  const [farmEditBoundary, setFarmEditBoundary] = useState([]);
  // 경계를 실제로 손댔을 때만 PUT payload 에 포함한다 — 안 그러면 이름만 고쳐도
  // 매번 그리드/경로가 재생성되고 수동 경로(LAST_MANUAL)가 초기화된다.
  const [farmEditBoundaryDirty, setFarmEditBoundaryDirty] = useState(false);
  const [farmEditMapSearch, setFarmEditMapSearch] = useState("");
  const [farmEditMapCenter, setFarmEditMapCenter] = useState(null);
  const [farmEditSubmitting, setFarmEditSubmitting] = useState(false);

  const [isDroneModalOpen, setIsDroneModalOpen] = useState(false);
  const [droneForm, setDroneForm] = useState(EMPTY_DRONE_FORM);
  const [droneModalError, setDroneModalError] = useState("");
  const [droneModalLockOwner, setDroneModalLockOwner] = useState(false);
  const [droneModalFromUserFlow, setDroneModalFromUserFlow] = useState(false);
  const [droneEditingId, setDroneEditingId] = useState(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_DRAFT);
  const [editFarmId, setEditFarmId] = useState("");
  const [editFarmName, setEditFarmName] = useState("");
  const [editFarmAddress, setEditFarmAddress] = useState("");
  const [editCropId, setEditCropId] = useState("");
  const [editConnectionId, setEditConnectionId] = useState(null);
  const [editDroneId, setEditDroneId] = useState("");
  const [editSmartPoleId, setEditSmartPoleId] = useState("");
  const [editDeviceType, setEditDeviceType] = useState("POLE");
  // 태양광큐브는 여러 대: 체크한 큐브 id 목록과 그중 fog 로 지정한 1대(없으면 ""). 폴은 editSmartPoleId 하나.
  const [editCubeIds, setEditCubeIds] = useState([]);
  const [editFogId, setEditFogId] = useState("");
  const [editConnectedDroneOption, setEditConnectedDroneOption] = useState(null);
  const [editConnectedSmartPoleOption, setEditConnectedSmartPoleOption] = useState(null);
  const [editModalError, setEditModalError] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);
  const editSavingRef = useRef(false);
  const [adminTab, setAdminTab] = useState("users");
  const [cropQuery, setCropQuery] = useState("");
  const [droneQuery, setDroneQuery] = useState("");
  const [droneStatusFilter, setDroneStatusFilter] = useState("ALL");
  const [smartPoleQuery, setSmartPoleQuery] = useState("");
  const [smartPoleStatusFilter, setSmartPoleStatusFilter] = useState("ALL");
  const [connectionQuery, setConnectionQuery] = useState("");
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [connections, setConnections] = useState([]);
  const [connectionSortKey, setConnectionSortKey] = useState("userId");
  const [connectionSortDir, setConnectionSortDir] = useState("asc");
  const [connectionColWidths, setConnectionColWidths] = useState([
    60,
    170,
    170,
    170,
    170,
    140,
  ]);
  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);
  const [connectionForm, setConnectionForm] = useState({
    userId: "",
    farmId: "",
    droneId: "",
    smartPoleId: "",
  });
  const [connectionModalError, setConnectionModalError] = useState("");
  const [connectionEditingIndex, setConnectionEditingIndex] = useState(null);

  const activeUsers = useMemo(
    () => users.filter((u) => u.accountStatus === "ACTIVE"),
    [users]
  );

  const isAdmin = useMemo(() => {
    const role = localStorage.getItem("role");
    return role && role.toUpperCase() === "ADMIN";
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/", { replace: true });
      return;
    }
  }, [isAdmin, navigate]);

  const loadUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const [{ data }] = await Promise.all([
        axios.get("/api/admin/users"),
        new Promise((r) => setTimeout(r, 300)),
      ]);
      setUsers(data?.data ?? []);
    } catch (err) {
      setError(err?.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCrops = async () => {
    setCropError("");
    setCropLoading(true);
    try {
      const [{ data }] = await Promise.all([
        axios.get("/api/admin/crops"),
        new Promise((r) => setTimeout(r, 300)),
      ]);
      const list = data?.data ?? [];
      setCrops(list);
      return list;
    } catch (err) {
      setCropError(err?.response?.data?.error?.message || err.message);
      return [];
    } finally {
      setCropLoading(false);
    }
  };

  const loadDrones = async () => {
    setDroneError("");
    setDroneLoading(true);
    try {
      const [{ data }] = await Promise.all([
        axios.get("/api/admin/drones"),
        new Promise((r) => setTimeout(r, 300)),
      ]);
      const list = data?.data ?? [];
      setDrones(list);
      return list;
    } catch (err) {
      setDroneError(err?.response?.data?.error?.message || err.message);
      return null;
    } finally {
      setDroneLoading(false);
    }
  };

  const loadFarms = async () => {
    setFarmLoading(true);
    setFarmError("");
    try {
      const [{ data }] = await Promise.all([
        axios.get("/api/admin/farms", { params: { size: 200 } }),
        new Promise((r) => setTimeout(r, 300)),
      ]);
      const list = data?.data?.content ?? [];
      setFarms(list);
      return list;
    } catch (err) {
      setFarmError(err?.response?.data?.error?.message || err.message);
      return null;
    } finally {
      setFarmLoading(false);
    }
  };

  const loadSmartPoles = async () => {
    setSmartPoleLoading(true);
    try {
      const [{ data }] = await Promise.all([
        axios.get("/api/admin/smartpoles"),
        new Promise((r) => setTimeout(r, 300)),
      ]);
      const list = data?.data ?? [];
      setSmartPoles(list);
      return list;
    } catch (e) {
      console.error("스마트폴 목록 조회 실패:", e);
      return null;
    } finally {
      setSmartPoleLoading(false);
    }
  };

  // connection 테이블 폐지: 농장(farm)이 단일 진실 소스. 드론/폴 배선은 farm_id 기준으로만 조회한다.
  // (별도 connection 목록 API 없음 — 이 함수는 하위 호환용 no-op)
  const loadConnections = async () => {
    setConnections([]);
    return [];
  };

  useEffect(() => {
    loadUsers();
    loadCrops();
    loadDrones();
    loadFarms();
    loadSmartPoles();
    loadConnections();
  }, []);

  useEffect(() => {
    if (!saveSuccessOpen) return undefined;
    const timer = window.setTimeout(() => {
      setSaveSuccessOpen(false);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [saveSuccessOpen]);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = users;

    if (q) {
      list = list.filter((u) => {
        const hay = [
          u.id,
          u.username,
          u.nickname,
          u.email,
          u.role,
          u.accountStatus,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }

    if (roleFilter !== "ALL") {
      list = list.filter((u) => u.role === roleFilter);
    }

    if (statusFilter !== "ALL") {
      list = list.filter((u) => u.accountStatus === statusFilter);
    }

    const sorted = [...list].sort((a, b) => {
      const av = a?.[sortKey];
      const bv = b?.[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return av - bv;
      }
      return String(av).localeCompare(String(bv));
    });

    return sortDir === "asc" ? sorted : sorted.reverse();
  }, [users, query, roleFilter, statusFilter, sortKey, sortDir]);

  const filteredCrops = useMemo(() => {
    const q = cropQuery.trim().toLowerCase();
    let list = crops;
    if (q) {
      list = list.filter((crop) => {
        const hay = [
          crop.cropId,
          crop.nameKo,
          crop.nameEn,
          crop.active ? "active" : "inactive",
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }

    const sorted = [...list].sort((a, b) => {
      const av = a?.[cropSortKey];
      const bv = b?.[cropSortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return av - bv;
      }
      return String(av).localeCompare(String(bv));
    });

    return cropSortDir === "asc" ? sorted : sorted.reverse();
  }, [crops, cropQuery, cropSortKey, cropSortDir]);

  const filteredDrones = useMemo(() => {
    const q = droneQuery.trim().toLowerCase();
    let list = drones;
    if (droneStatusFilter !== "ALL") {
      list = list.filter((drone) => drone.droneStatus === droneStatusFilter);
    }
    if (q) {
      list = list.filter((drone) => {
        const hay = [
          drone.id,
          drone.droneName,
          drone.serialNum,
          drone.userNickname,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }

    const sorted = [...list].sort((a, b) => {
      const av = a?.[droneSortKey];
      const bv = b?.[droneSortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return av - bv;
      }
      return String(av).localeCompare(String(bv));
    });

    return droneSortDir === "asc" ? sorted : sorted.reverse();
  }, [drones, droneQuery, droneStatusFilter, droneSortKey, droneSortDir]);

  const filteredFarms = useMemo(() => {
    const q = farmQuery.trim().toLowerCase();
    let list = farms;
    if (q) {
      list = list.filter((farm) => {
        const hay = [
          farm.id,
          farm.name,
          farm.address,
          farm.ownerNickname,
          farm.cropName,
          farm.area,
        ]
          .filter((v) => v !== null && v !== undefined)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }

    const sorted = [...list].sort((a, b) => {
      const av = a?.[farmSortKey];
      const bv = b?.[farmSortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return av - bv;
      }
      return String(av).localeCompare(String(bv));
    });

    return farmSortDir === "asc" ? sorted : sorted.reverse();
  }, [farms, farmQuery, farmSortKey, farmSortDir]);

  const resolveSmartPoleDroneId = (smartPole) => {
    if (!smartPole) return "";
    if (smartPole.droneId != null) return String(smartPole.droneId);
    const serial = smartPole.droneSerialNum;
    if (!serial) return "";
    const matched = drones.find((drone) => drone.serialNum === serial);
    return matched?.id != null ? String(matched.id) : "";
  };

  // 스마트폴 탭과 태양광큐브 탭은 같은 smartPoles 배열을 device_type 으로 걸러
  // 쓴다. 목록 API 를 따로 호출하지 않으므로 요청 수가 늘지 않는다.
  const filterDevicesByType = useCallback((deviceType) => {
    const q = smartPoleQuery.trim().toLowerCase();
    let list = smartPoles.filter(
      (device) => normalizeDeviceType(device.deviceType) === deviceType
    );
    if (smartPoleStatusFilter !== "ALL") {
      list = list.filter((smartPole) => smartPole.status === smartPoleStatusFilter);
    }
    if (q) {
      list = list.filter((smartPole) => {
        const hay = [
          smartPole.id,
          smartPole.name,
          smartPole.serialNum,
          smartPole.userNickname,
          smartPole.farmName,
        ]
          .filter((v) => v !== null && v !== undefined)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }

    const sorted = [...list].sort((a, b) => {
      const av = a?.[smartPoleSortKey];
      const bv = b?.[smartPoleSortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return av - bv;
      }
      return String(av).localeCompare(String(bv));
    });

    return smartPoleSortDir === "asc" ? sorted : sorted.reverse();
  }, [
    smartPoles,
    smartPoleQuery,
    smartPoleStatusFilter,
    smartPoleSortKey,
    smartPoleSortDir,
  ]);

  const filteredSmartPoles = useMemo(
    () => filterDevicesByType("POLE"),
    [filterDevicesByType]
  );
  const filteredSmartCubes = useMemo(
    () => filterDevicesByType("CUBE"),
    [filterDevicesByType]
  );

  // 현재 보고 있는 장치 탭(스마트폴 / 태양광큐브)의 파생값.
  // 검색어·정렬·컬럼 폭 상태는 두 탭이 공유한다(탭을 옮겨도 조건이 유지된다).
  const deviceTabType = adminTab === "smartCubes" ? "CUBE" : "POLE";
  const deviceTabLabel = DEVICE_LABEL[deviceTabType];
  const deviceTabRows =
    deviceTabType === "CUBE" ? filteredSmartCubes : filteredSmartPoles;

  const filteredConnections = useMemo(() => {
    const q = connectionQuery.trim().toLowerCase();
    let list = connections;
    if (q) {
      list = list.filter((conn) => {
        const hay = [
          conn.id,
          conn.userNickname,
          conn.farmName,
          conn.droneName,
          conn.smartPoleName,
        ]
          .filter((v) => v !== null && v !== undefined)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }
    const sorted = [...list].sort((a, b) => {
      const av = a?.[connectionSortKey];
      const bv = b?.[connectionSortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      return String(av).localeCompare(String(bv));
    });
    return connectionSortDir === "asc" ? sorted : sorted.reverse();
  }, [connections, connectionQuery, connectionSortKey, connectionSortDir]);

  const connectionUserFarms = useMemo(() => {
    if (!connectionForm.userId) return [];
    return farms.filter((f) => String(f.ownerId) === String(connectionForm.userId));
  }, [farms, connectionForm.userId]);

  const connectionUserDrones = useMemo(() => {
    if (!connectionForm.userId) return [];
    return drones.filter((d) => String(d.userId) === String(connectionForm.userId));
  }, [drones, connectionForm.userId]);

  const connectionUserSmartPoles = useMemo(() => {
    if (!connectionForm.userId) return [];
    return smartPoles.filter((sp) => String(sp.userId) === String(connectionForm.userId));
  }, [smartPoles, connectionForm.userId]);

  const connectedFarmIds = useMemo(
    () => new Set(connections.map((c) => c.farmId).filter(Boolean)),
    [connections]
  );
  const connectedDroneIds = useMemo(
    () => new Set(connections.map((c) => c.droneId).filter(Boolean)),
    [connections]
  );
  const connectedSmartPoleIds = useMemo(
    () => new Set(connections.map((c) => c.smartPoleId).filter(Boolean)),
    [connections]
  );

  const hasConnectedId = (idSet, id) =>
    idSet.has(id) || idSet.has(Number(id)) || idSet.has(String(id));

  const getConnectedFarmIdByDevice = (deviceKey, deviceId) =>
    connections.find((connection) => String(connection?.[deviceKey]) === String(deviceId))
      ?.farmId ?? null;

  const getConnectionId = (connection) =>
    connection?.id ?? connection?.connectionId ?? connection?.connection_id ?? null;

  const resolveFarmCropId = (farm, cropSource = crops) => {
    if (!farm) return "";
    if (farm.cropId != null) return String(farm.cropId);
    if (!farm.cropName) return "";
    const matchedCrop = cropSource.find((crop) =>
      [crop.nameKo, crop.nameEn]
        .filter(Boolean)
        .some((value) => String(value) === String(farm.cropName))
    );
    return matchedCrop?.cropId != null ? String(matchedCrop.cropId) : "";
  };

  const editUserFarms = useMemo(() => {
    if (!editUser) return [];
    return farms.filter((farm) => String(farm.ownerId) === String(editUser.id));
  }, [farms, editUser]);

  const applyEditFarmSelection = (
    farmId,
    farmSource = farms,
    connectionSource = connections,
    smartPoleSource = smartPoles,
    targetUser = editUser,
    cropSource = crops,
    droneSource = drones
  ) => {
    if (!farmId) {
      setEditFarmId("");
      setEditFarmName("");
      setEditFarmAddress("");
      setEditCropId("");
      setEditConnectionId(null);
      setEditDroneId("");
      setEditSmartPoleId("");
      setEditDeviceType("POLE");
      setEditCubeIds([]);
      setEditFogId("");
      setEditConnectedDroneOption(null);
      setEditConnectedSmartPoleOption(null);
      return;
    }
    const selectedFarm = farmSource.find((farm) => String(farm.id) === String(farmId));
    const selectedConnection = connectionSource.find(
      (connection) => String(connection.farmId) === String(farmId)
    );
    // 농장에 붙은 에너지 장치 전부(V11 부터 큐브는 여러 대). 소유자가 달라도 붙어 있으면 보여준다 —
    // 예전 데이터를 숨기면 화면엔 없는데 농장엔 있는 장치가 생긴다.
    const selectedDevices = smartPoleSource.filter(
      (smartPole) => String(smartPole.farmId) === String(farmId)
    );
    const selectedDrone = droneSource.find((drone) => {
      if (String(drone.userId) !== String(targetUser?.id)) return false;
      return String(drone.farmId) === String(farmId);
    });
    const resolvedDroneId = selectedConnection
      ? selectedConnection.droneId
      : selectedFarm?.droneId ?? selectedDrone?.id ?? null;
    // 라디오 초기값은 "실제로 붙어 있는 장치의 타입"이어야 한다. 안 맞추면 큐브가
    // 배선된 농장을 열었는데 라디오는 스마트폴에 있는 모순 상태가 된다.
    const deviceType = normalizeDeviceType(selectedDevices[0]?.deviceType);
    const selectedSmartPole = deviceType === "POLE" ? selectedDevices[0] ?? null : null;
    const resolvedSmartPoleId = selectedConnection
      ? selectedConnection.smartPoleId
      : selectedSmartPole?.id ?? null;

    setEditFarmId(selectedFarm ? String(selectedFarm.id) : "");
    setEditFarmName(selectedFarm?.name ?? "");
    setEditFarmAddress(selectedFarm?.address ?? "");
    setEditCropId(resolveFarmCropId(selectedFarm, cropSource));
    setEditConnectionId(getConnectionId(selectedConnection));
    setEditDroneId(resolvedDroneId != null ? String(resolvedDroneId) : "");
    setEditDeviceType(deviceType);
    setEditSmartPoleId(
      deviceType === "POLE" && resolvedSmartPoleId != null ? String(resolvedSmartPoleId) : ""
    );
    // 큐브: 붙어 있는 큐브 전부 체크, fog 라디오는 관리자 설정값(nodeRole)대로.
    const cubes = deviceType === "CUBE" ? selectedDevices : [];
    setEditCubeIds(cubes.map((cube) => String(cube.id)));
    setEditFogId(String(cubes.find((cube) => cube.nodeRole === "FOG")?.id ?? ""));
    setEditConnectedDroneOption(
      resolvedDroneId != null
        ? {
            id: String(resolvedDroneId),
            name: selectedConnection?.droneName ?? selectedFarm?.droneName ?? selectedDrone?.droneName ?? "",
            serialNum:
              selectedConnection?.droneSerialNum ??
              selectedFarm?.droneSerialNum ??
              selectedDrone?.serialNum ??
              "",
          }
        : null
    );
    setEditConnectedSmartPoleOption(
      resolvedSmartPoleId != null
        ? {
            id: String(resolvedSmartPoleId),
            name: selectedConnection?.smartPoleName ?? selectedSmartPole?.name ?? "",
            serialNum:
              selectedConnection?.smartPoleSerialNum ??
              selectedSmartPole?.serialNum ??
              "",
          }
        : null
    );
  };

  // 타입만 바꾸고 선택된 ID 를 그대로 두면 "화면엔 스마트큐브, 저장되는 건 POLE" 이라는
  // 조용한 오배선이 생긴다. 타입 변경은 반드시 이 함수 하나만 거치게 한다.
  const changeEditDeviceType = (nextType) => {
    const next = normalizeDeviceType(nextType);
    if (next === editDeviceType) return;
    setEditDeviceType(next);
    setEditSmartPoleId("");
    setEditCubeIds([]);
    setEditFogId("");
    setEditConnectedSmartPoleOption(null);
  };

  const toggleEditCube = (id) => {
    const wasChecked = editCubeIds.includes(id);
    setEditCubeIds((prev) => (wasChecked ? prev.filter((v) => v !== id) : [...prev, id]));
    // 체크를 풀면 fog 지정도 같이 푼다 — fog 가 선택 목록 밖에 남으면 서버가 저장을 거부한다.
    if (wasChecked && editFogId === id) setEditFogId("");
  };

  const editUserDrones = useMemo(() => {
    if (!editUser) return [];
    const options = drones.filter((drone) => {
      if (editDroneId && String(drone.id) === String(editDroneId)) return true;
      if (String(drone.userId) !== String(editUser.id)) return false;
      return true;
    });
    const currentId = editConnectedDroneOption?.id;
    if (currentId && !options.some((drone) => String(drone.id) === String(currentId))) {
      options.unshift({
        id: Number(currentId),
        droneName: editConnectedDroneOption.name || "existing drone",
        serialNum: editConnectedDroneOption.serialNum || "",
        userId: editUser.id,
        farmId: editFarmId ? Number(editFarmId) : null,
      });
    }
    return options;
  }, [drones, editUser, editFarmId, editDroneId, editConnectedDroneOption]);

  const editUserSmartPoles = useMemo(() => {
    if (!editUser) return [];
    const options = smartPoles.filter((smartPole) => {
      // 타입 검사를 가장 먼저. 아래 "현재 선택은 무조건 통과" 규칙이 타입 필터를
      // 뚫고 반대 타입 장치를 목록에 남기는 것을 막는다.
      if (normalizeDeviceType(smartPole.deviceType) !== editDeviceType) return false;
      if (editSmartPoleId && String(smartPole.id) === String(editSmartPoleId)) return true;
      // 이미 이 농장에 붙어 있는 큐브는 소유자가 달라도 목록에 남긴다(해제할 수 있어야 한다).
      if (editDeviceType === "CUBE" && editCubeIds.includes(String(smartPole.id))) return true;
      if (String(smartPole.userId) !== String(editUser.id)) return false;
      return true;
    });
    const currentId = editConnectedSmartPoleOption?.id;
    if (
      currentId &&
      !options.some((smartPole) => String(smartPole.id) === String(currentId))
    ) {
      options.unshift({
        id: Number(currentId),
        name:
          editConnectedSmartPoleOption.name ||
          `existing ${DEVICE_LABEL[editDeviceType]}`,
        serialNum: editConnectedSmartPoleOption.serialNum || "",
        userId: editUser.id,
        farmId: editFarmId ? Number(editFarmId) : null,
        deviceType: editDeviceType,
      });
    }
    return options;
  }, [
    smartPoles,
    editUser,
    editFarmId,
    editSmartPoleId,
    editCubeIds,
    editDeviceType,
    editConnectedSmartPoleOption,
  ]);

  // 저장 전에 결과 구성을 미리 보여준다. 서버 검증(FARM_FOG_INVALID 등)과 같은 규칙이라
  // 여기서 걸리는 조합은 서버에서도 거부된다.
  const wirePreview = useMemo(() => {
    if (editDeviceType !== "CUBE") return { text: "", error: false };
    const n = editCubeIds.length;
    if (n === 0) return { text: "저장하면: 태양광큐브 연결 해제 (연결된 에너지 장치 없음)", error: false };
    if (editFogId && !editCubeIds.includes(editFogId)) {
      return { text: "fog로 고른 큐브가 선택 목록에 없습니다.", error: true };
    }
    if (editFogId && n < 2) {
      return { text: "fog는 묶을 edge가 있어야 합니다. 큐브를 2대 이상 고르거나 fog 없음을 선택하세요.", error: true };
    }
    if (editFogId) {
      const fog = smartPoles.find((sp) => String(sp.id) === editFogId);
      return { text: `저장하면: fog 공유 모드 · fog ${fog?.name ?? editFogId} + edge ${n - 1}대`, error: false };
    }
    return { text: `저장하면: 엣지만 모드 · edge ${n}대, 전력 공유 없음`, error: false };
  }, [editDeviceType, editCubeIds, editFogId, smartPoles]);

  // 큐브/폴 수정 모달이 열어 둔 장치. 제목과 "현재 농장" 안내에 쓴다.
  const smartPoleEditTarget = useMemo(
    () => smartPoles.find((sp) => sp.id === smartPoleEditTargetId) ?? null,
    [smartPoles, smartPoleEditTargetId]
  );

  const openConnectionModal = () => {
    setConnectionForm({ userId: "", farmId: "", droneId: "", smartPoleId: "" });
    setConnectionModalError("");
    setConnectionEditingIndex(null);
    setIsConnectionModalOpen(true);
  };

  const openConnectionEditModal = (connectionId) => {
    const conn = connections.find((c) => c.id === connectionId);
    if (!conn) return;
    setConnectionForm({
      userId: String(conn.userId ?? ""),
      farmId: String(conn.farmId ?? ""),
      droneId: String(conn.droneId ?? ""),
      smartPoleId: String(conn.smartPoleId ?? ""),
    });
    setConnectionModalError("");
    setConnectionEditingIndex(connectionId);
    setIsConnectionModalOpen(true);
  };

  const closeConnectionModal = () => {
    setIsConnectionModalOpen(false);
    setConnectionModalError("");
    setConnectionEditingIndex(null);
    setConnectionForm({ userId: "", farmId: "", droneId: "", smartPoleId: "" });
  };

  const submitConnection = async () => {
    const isEditing = connectionEditingIndex != null;

    if (!connectionForm.userId) {
      setConnectionModalError("사용자를 선택하세요.");
      return;
    }
    if (!connectionForm.farmId) {
      setConnectionModalError("농장을 선택하세요.");
      return;
    }
    if (!isEditing && !connectionForm.droneId) {
      setConnectionModalError("드론을 선택하세요.");
      return;
    }
    if (!isEditing && !connectionForm.smartPoleId) {
      setConnectionModalError("스마트폴을 선택하세요.");
      return;
    }

    const droneNone = connectionForm.droneId === "__NONE__";
    const spNone = connectionForm.smartPoleId === "__NONE__";

    const payload = {
      userId: Number(connectionForm.userId),
      farmId: Number(connectionForm.farmId),
      droneId: droneNone || !connectionForm.droneId ? null : Number(connectionForm.droneId),
      smartPoleId: spNone || !connectionForm.smartPoleId ? null : Number(connectionForm.smartPoleId),
    };

    try {
      if (isEditing) {
        await axios.put(`/api/admin/connections/${connectionEditingIndex}`, payload);
      } else {
        await axios.post("/api/admin/connections", payload);
      }
      await loadConnections();
      closeConnectionModal();
    } catch (err) {
      setConnectionModalError(err?.response?.data?.error?.message || err.message);
    }
  };

  const [connectionDeleteTarget, setConnectionDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const deleteConnection = async (connectionId) => {
    try {
      await axios.delete(`/api/admin/connections/${connectionId}`);
      await loadConnections();
    } catch (err) {
      console.error("연결 삭제 실패:", err);
    } finally {
      setConnectionDeleteTarget(null);
    }
  };

  const handleSort = (key, target) => {
    if (target === "users") {
      if (sortKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
      return;
    }
    if (target === "crops") {
      if (cropSortKey === key) {
        setCropSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setCropSortKey(key);
        setCropSortDir("asc");
      }
      return;
    }
    if (target === "drones") {
      if (droneSortKey === key) {
        setDroneSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setDroneSortKey(key);
        setDroneSortDir("asc");
      }
      return;
    }
    if (target === "smartPoles") {
      if (smartPoleSortKey === key) {
        setSmartPoleSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSmartPoleSortKey(key);
        setSmartPoleSortDir("asc");
      }
      return;
    }
    if (target === "connections") {
      if (connectionSortKey === key) {
        setConnectionSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setConnectionSortKey(key);
        setConnectionSortDir("asc");
      }
      return;
    }
    if (target === "farms") {
      if (farmSortKey === key) {
        setFarmSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setFarmSortKey(key);
        setFarmSortDir("asc");
      }
    }
  };

  const makeGridTemplate = (widths) =>
    widths.map((width) => `${Math.max(width, 60)}px`).join(" ");

  const userGridTemplate = useMemo(
    () => makeGridTemplate(userColWidths),
    [userColWidths]
  );
  const cropGridTemplate = useMemo(
    () => makeGridTemplate(cropColWidths),
    [cropColWidths]
  );
  const droneGridTemplate = useMemo(
    () => makeGridTemplate(droneColWidths),
    [droneColWidths]
  );
  // 역할 열(5번째)은 태양광큐브 탭에만 있다. 폭 배열은 두 탭이 공유하고 템플릿만 거른다.
  const smartPoleGridTemplate = useMemo(
    () =>
      makeGridTemplate(
        deviceTabType === "CUBE"
          ? smartPoleColWidths
          : smartPoleColWidths.filter((_, index) => index !== 5)
      ),
    [smartPoleColWidths, deviceTabType]
  );
  const farmGridTemplate = useMemo(
    () => makeGridTemplate(farmColWidths),
    [farmColWidths]
  );
  const connectionGridTemplate = useMemo(
    () => makeGridTemplate(connectionColWidths),
    [connectionColWidths]
  );

  const startResize = (table, index) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    const startWidths =
      table === "users"
        ? userColWidths
        : table === "crops"
        ? cropColWidths
        : table === "drones"
        ? droneColWidths
        : table === "farms"
        ? farmColWidths
        : table === "smartPoles"
        ? smartPoleColWidths
        : connectionColWidths;
    setResizeState({
      table,
      index,
      startX: event.clientX,
      startWidths,
    });
  };

  useEffect(() => {
    if (!resizeState) return;

    const handleMove = (event) => {
      const delta = event.clientX - resizeState.startX;
      const minWidth = 60;
      const next = [...resizeState.startWidths];
      next[resizeState.index] = Math.max(
        minWidth,
        resizeState.startWidths[resizeState.index] + delta
      );
      if (resizeState.table === "users") {
        setUserColWidths(next);
      } else if (resizeState.table === "crops") {
        setCropColWidths(next);
      } else if (resizeState.table === "drones") {
        setDroneColWidths(next);
      } else if (resizeState.table === "farms") {
        setFarmColWidths(next);
      } else if (resizeState.table === "smartPoles") {
        setSmartPoleColWidths(next);
      } else {
        setConnectionColWidths(next);
      }
    };

    const handleUp = () => {
      setResizeState(null);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [resizeState]);

  const renderSortLabel = (label, key, target, activeKey, activeDir) => (
    <button
      type="button"
      className={`admin-sort-btn ${activeKey === key ? "is-active" : ""}`}
      onClick={() => handleSort(key, target)}
    >
      <span>{label}</span>
      <span className="admin-sort-icons">
        <span
          className={`admin-sort-triangle up ${
            activeKey === key && activeDir === "asc" ? "is-active" : ""
          }`}
        />
        <span
          className={`admin-sort-triangle down ${
            activeKey === key && activeDir === "desc" ? "is-active" : ""
          }`}
        />
      </span>
    </button>
  );

  const startEdit = (user) => {
    setEditingId(user.id);
    setDraft({
      nickname: user.nickname ?? "",
      email: user.email ?? "",
      role: user.role ?? "USER",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setError("");
    try {
      const payload = {
        nickname: draft.nickname?.trim() || null,
        email: draft.email?.trim() || null,
        role: draft.role,
      };
      const { data } = await axios.patch(
        `/api/admin/users/${editingId}`,
        payload
      );
      const updated = data?.data;
      if (updated) {
        setUsers((prev) =>
          prev.map((u) => (u.id === updated.id ? updated : u))
        );
      }
      cancelEdit();
    } catch (err) {
      setError(err?.response?.data?.error?.message || err.message);
    }
  };

  const openCreateModal = () => {
    setIsModalOpen(true);
    setModalStep(1);
    setModalError("");
    setUserFieldErrors({});
    setIsSubmitting(false);
    setUserForm(EMPTY_USER_FORM);
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setFarmForm(EMPTY_FARM_FORM);
    setBoundaryPoints([]);
    setCreatedUser(null);
    createdUserSnapshotRef.current = null;
    setCreatedFarmId(null);
  };

  const closeCreateModal = () => {
    if (isSubmitting) return;
    setIsModalOpen(false);
    setModalError("");
    setCreatedUser(null);
    createdUserSnapshotRef.current = null;
    setCreatedFarmId(null);
  };

  // BE AdminCreateUserRequest 의 검증 규칙(@Size/@Email)을 1단계에서 미리 검사해
  // 위반 필드를 빨간 테두리로 표시한다. 안 하면 2단계에서 경계까지 다 그린 뒤에야
  // BE 400 이 떨어져 이전 단계로 돌아가 다시 고쳐야 한다.
  const [userFieldErrors, setUserFieldErrors] = useState({});
  const clearUserFieldError = (field) =>
    setUserFieldErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));

  const validateUserFields = () => {
    const errors = {};
    const username = userForm.username.trim();
    if (!username) errors.username = "아이디를 입력하세요.";
    else if (username.length < 3 || username.length > 50)
      errors.username = "아이디는 3~50자로 입력하세요.";
    if (!userForm.password) errors.password = "비밀번호를 입력하세요.";
    else if (userForm.password.length < 8 || userForm.password.length > 72)
      errors.password = "비밀번호는 8~72자로 입력하세요.";
    if (!confirmPassword) errors.confirmPassword = "비밀번호 확인을 입력하세요.";
    else if (userForm.password !== confirmPassword)
      errors.confirmPassword = "비밀번호가 일치하지 않습니다.";
    const nickname = userForm.nickname.trim();
    if (!nickname) errors.nickname = "닉네임을 입력하세요.";
    else if (nickname.length < 2 || nickname.length > 50)
      errors.nickname = "닉네임은 2~50자로 입력하세요.";
    const email = userForm.email.trim();
    if (!email) errors.email = "이메일을 입력하세요.";
    else if (email.length > 320 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      errors.email = "이메일 형식이 올바르지 않습니다.";
    // BE 에 phone 제약이 없어 이상한 값이 저장 시점 500 으로 터진다 — FE 에서 선제 차단
    const phone = userForm.phone.trim();
    if (phone && !/^[0-9+\-() ]{7,20}$/.test(phone))
      errors.phone = "전화번호 형식이 올바르지 않습니다. (숫자/하이픈 7~20자)";
    return errors;
  };

  const validateUserStep = () => {
    const errors = validateUserFields();
    setUserFieldErrors(errors);
    return Object.values(errors).find(Boolean) || "";
  };

  // 사용자 POST 성공 후 농장 POST 가 실패해도 계정은 이미 서버에 존재한다.
  // - 생성 즉시 users 목록에 반영해 "보이지 않는 고아 계정"을 없앤다.
  // - 1단계를 고쳐 재제출하면 PATCH 가능한 필드(닉네임/이메일/role)는 서버에 반영하고,
  //   PATCH API 가 없는 필드(아이디/비밀번호/전화번호)의 변경은 명시적으로 막는다
  //   (조용히 무시하면 관리자는 고쳐졌다고 믿게 됨).
  const createdUserSnapshotRef = useRef(null);

  const createUserIfNeeded = async () => {
    const desired = {
      username: userForm.username.trim(),
      password: userForm.password,
      nickname: userForm.nickname.trim(),
      email: userForm.email.trim(),
      phone: userForm.phone.trim() || null,
      role: userForm.role,
    };
    if (createdUser) {
      const snap = createdUserSnapshotRef.current || {};
      if (
        desired.username !== snap.username ||
        desired.password !== snap.password ||
        desired.phone !== snap.phone
      ) {
        throw new Error(
          "이미 생성된 계정의 아이디/비밀번호/전화번호는 이 화면에서 변경할 수 없습니다. 완료 후 사용자 목록의 수정에서 변경하세요."
        );
      }
      if (
        desired.nickname !== snap.nickname ||
        desired.email !== snap.email ||
        desired.role !== snap.role
      ) {
        const { data } = await axios.patch(
          `/api/admin/users/${createdUser.id}`,
          {
            nickname: desired.nickname,
            email: desired.email,
            role: desired.role,
          }
        );
        const updated = data?.data;
        if (updated) {
          createdUserSnapshotRef.current = desired;
          setCreatedUser(updated);
          setUsers((prev) =>
            prev.map((u) => (u.id === updated.id ? updated : u))
          );
          return updated;
        }
      }
      return createdUser;
    }
    const { data } = await axios.post("/api/admin/users", desired);
    const user = data?.data;
    if (!user) throw new Error("사용자 생성에 실패했습니다.");
    createdUserSnapshotRef.current = desired;
    setCreatedUser(user);
    setUsers((prev) => [user, ...prev]);
    return user;
  };
  const goToNextStep = () => {
    const msg = validateUserStep();
    if (msg) {
      setModalError(msg);
      return;
    }
    setModalError("");
    setModalStep(2);
  };

  const handleMapSearch = () => {
    const query = mapSearchQuery.trim();
    if (!query || !window.google?.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: query }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        setMapCenter({ lat: loc.lat(), lng: loc.lng() });
      }
    });
  };

  const normalizeBoundary = (points) => {
    if (!points.length) return [];
    const first = points[0];
    const last = points[points.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) return points;
    return [...points, first];
  };

  const submitUserAndFarm = async () => {
    const msg = validateUserStep();
    if (msg) {
      setModalError(msg);
      setModalStep(1);
      return;
    }

    if (!farmForm.name.trim()) {
      setModalError("농장 이름을 입력하세요.");
      return;
    }
    if (!farmForm.address.trim()) {
      setModalError("주소를 입력하세요.");
      return;
    }
    if (!farmForm.cropId) {
      setModalError("작물을 선택하세요.");
      return;
    }
    if (boundaryPoints.length < 3) {
      setModalError("경계는 최소 3개의 점이 필요합니다.");
      return;
    }

    setModalError("");
    setIsSubmitting(true);
    try {
      const user = await createUserIfNeeded();
      const farmPayload = {
        ownerId: user.id,
        cropId: Number(farmForm.cropId),
        name: farmForm.name.trim(),
        address: farmForm.address.trim(),
        boundary: normalizeBoundary(boundaryPoints),
      };

      const { data } = await axios.post("/api/admin/farms", farmPayload);
      const createdFarm = data?.data;
      if (createdFarm?.id) {
        setCreatedFarmId(createdFarm.id);
      }

      await loadFarms();

      setIsModalOpen(false);
      setModalStep(1);
      setUserForm(EMPTY_USER_FORM);
      setConfirmPassword("");
      setShowPassword(false);
      setShowConfirmPassword(false);
      setFarmForm(EMPTY_FARM_FORM);
      setBoundaryPoints([]);
      setCreatedUser(null);
      createdUserSnapshotRef.current = null;
      setCreatedFarmId(null);
    } catch (err) {
      setModalError(err?.response?.data?.error?.message || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openFarmModal = (options = {}) => {
    const { presetOwnerId } = options;
    setFarmModalForm({
      name: "",
      address: "",
      cropId: "",
      ownerId: presetOwnerId ? String(presetOwnerId) : "",
    });
    setFarmModalError("");
    setFarmModalBoundary([]);
    setFarmModalMapSearch("");
    setFarmModalMapCenter(null);
    setIsFarmModalOpen(true);
  };

  const closeFarmModal = () => {
    if (farmModalSubmitting) return;
    setIsFarmModalOpen(false);
    setFarmModalError("");
  };

  const handleFarmModalMapSearch = () => {
    const q = farmModalMapSearch.trim();
    if (!q || !window.google?.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: q }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        setFarmModalMapCenter({ lat: loc.lat(), lng: loc.lng() });
      }
    });
  };

  const submitFarmModal = async () => {
    if (!farmModalForm.ownerId) {
      setFarmModalError("소유자를 선택하세요.");
      return;
    }
    if (!farmModalForm.name.trim()) {
      setFarmModalError("농장 이름을 입력하세요.");
      return;
    }
    if (!farmModalForm.address.trim()) {
      setFarmModalError("주소를 입력하세요.");
      return;
    }
    if (!farmModalForm.cropId) {
      setFarmModalError("작물을 선택하세요.");
      return;
    }
    if (farmModalBoundary.length < 3) {
      setFarmModalError("경계는 최소 3개의 점이 필요합니다.");
      return;
    }

    setFarmModalError("");
    setFarmModalSubmitting(true);
    try {
      const payload = {
        ownerId: Number(farmModalForm.ownerId),
        cropId: Number(farmModalForm.cropId),
        name: farmModalForm.name.trim(),
        address: farmModalForm.address.trim(),
        boundary: normalizeBoundary(farmModalBoundary),
      };
      const { data } = await axios.post("/api/admin/farms", payload);
      const createdFarm = data?.data;
      const latestFarms = await loadFarms();
      if (
        isEditModalOpen &&
        editUser &&
        createdFarm?.id &&
        String(farmModalForm.ownerId) === String(editUser.id)
      ) {
        applyEditFarmSelection(
          createdFarm.id,
          latestFarms ?? farms,
          connections,
          smartPoles,
          editUser,
          crops
        );
      }
      setIsFarmModalOpen(false);
    } catch (err) {
      setFarmModalError(err?.response?.data?.error?.message || err.message);
    } finally {
      setFarmModalSubmitting(false);
    }
  };

  const deleteFarm = async (farmId) => {
    setFarmError("");
    try {
      await axios.delete(`/api/admin/farms/${farmId}`);
      setFarms((prev) => prev.filter((f) => f.id !== farmId));
      // BE deleteFarmCascade 가 드론/폴 배선(farm_id)을 해제하므로 함께 갱신
      await Promise.all([loadDrones(), loadSmartPoles()]);
    } catch (err) {
      setFarmError(err?.response?.data?.error?.message || err.message);
    }
  };

  // 수정 버튼 연타로 GET 두 개가 엇갈려도 마지막 클릭만 반영한다.
  // 대상 id 를 클릭 즉시 세팅하면 늦게 도착한 이전 응답이 폼만 덮어써
  // "B 농장에 A 내용 저장" 사고가 난다 — id 와 폼은 응답 도착 후 함께 세팅.
  const farmEditReqSeqRef = useRef(0);

  const openFarmEditModal = async (farmId) => {
    const seq = ++farmEditReqSeqRef.current;
    setFarmEditError("");
    setFarmEditMapSearch("");
    setFarmEditMapCenter(null);
    try {
      const { data } = await axios.get(`/api/admin/farms/${farmId}`);
      if (seq !== farmEditReqSeqRef.current) return; // 이전 클릭의 늦은 응답 폐기
      const farm = data?.data;
      setFarmEditTargetId(farmId);
      setFarmEditForm({
        name: farm?.name || "",
        address: farm?.address || "",
        cropId: farm?.cropId != null ? String(farm.cropId) : "",
        ownerId: farm?.ownerId != null ? String(farm.ownerId) : "",
      });
      const vertices = farm?.boundary?.vertices ?? [];
      setFarmEditBoundary(
        vertices.map((v) => [Number(v.latitude), Number(v.longitude)])
      );
      setFarmEditBoundaryDirty(false);
      if (vertices.length > 0) {
        setFarmEditMapCenter({
          lat: Number(vertices[0].latitude),
          lng: Number(vertices[0].longitude),
        });
      }
      setIsFarmEditModalOpen(true);
    } catch (err) {
      if (seq !== farmEditReqSeqRef.current) return;
      setFarmError(err?.response?.data?.error?.message || err.message);
    }
  };

  const closeFarmEditModal = () => {
    if (farmEditSubmitting) return;
    setIsFarmEditModalOpen(false);
    setFarmEditError("");
  };

  const handleFarmEditMapSearch = () => {
    const q = farmEditMapSearch.trim();
    if (!q || !window.google?.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: q }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        setFarmEditMapCenter({ lat: loc.lat(), lng: loc.lng() });
      }
    });
  };

  const submitFarmEditModal = async () => {
    if (!farmEditForm.ownerId) {
      setFarmEditError("소유자를 선택하세요.");
      return;
    }
    if (!farmEditForm.name.trim()) {
      setFarmEditError("농장 이름을 입력하세요.");
      return;
    }
    if (!farmEditForm.address.trim()) {
      setFarmEditError("주소를 입력하세요.");
      return;
    }
    if (!farmEditForm.cropId) {
      setFarmEditError("작물을 선택하세요.");
      return;
    }

    // 경계를 손댄 경우에만 포함 — 보내면 BE 가 그리드/경로를 재생성한다
    let boundaryPayload = null;
    if (farmEditBoundaryDirty) {
      if (farmEditBoundary.length < 3) {
        setFarmEditError("경계는 최소 3개의 점이 필요합니다.");
        return;
      }
      if (
        !window.confirm(
          "경계를 변경하면 자동 경로(웨이포인트)가 재생성되고 수동 경로는 초기화됩니다. 계속할까요?"
        )
      ) {
        return;
      }
      boundaryPayload = normalizeBoundary(farmEditBoundary);
    }

    setFarmEditError("");
    setFarmEditSubmitting(true);
    try {
      const payload = {
        name: farmEditForm.name.trim(),
        address: farmEditForm.address.trim(),
        cropId: Number(farmEditForm.cropId),
        userId: Number(farmEditForm.ownerId),
      };
      if (boundaryPayload) payload.boundary = boundaryPayload;
      await axios.put(`/api/admin/farms/${farmEditTargetId}`, payload);
      await loadFarms();
      setIsFarmEditModalOpen(false);
    } catch (err) {
      setFarmEditError(err?.response?.data?.error?.message || err.message);
    } finally {
      setFarmEditSubmitting(false);
    }
  };

  const deleteUser = async (userId) => {
    setError("");
    try {
      await axios.delete(`/api/admin/users/${userId}`);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      // BE 가 농장 소유 해제·드론 비활성화·폴 사용자 해제까지 수행하므로 관련 목록도 갱신
      await Promise.all([loadFarms(), loadDrones(), loadSmartPoles()]);
    } catch (err) {
      setError(err?.response?.data?.error?.message || err.message);
    }
  };

  const openCropModal = (options = {}) => {
    const { fromEditFlow } = options;
    setCropModalError("");
    setCropEditingId(null);
    setCropModalFromEditFlow(Boolean(fromEditFlow));
    setCropForm(EMPTY_CROP_FORM);
    setIsCropModalOpen(true);
  };

  const openCropEditModal = (crop) => {
    if (!crop?.cropId) return;
    setCropModalError("");
    setCropModalFromEditFlow(false);
    setCropEditingId(crop.cropId);
    setCropForm({
      nameKo: crop.nameKo ?? "",
      nameEn: crop.nameEn ?? "",
    });
    setIsCropModalOpen(true);
  };

  const closeCropModal = () => {
    setIsCropModalOpen(false);
    setCropModalError("");
    setCropEditingId(null);
    setCropModalFromEditFlow(false);
    setCropForm(EMPTY_CROP_FORM);
  };

  // 등록/저장 버튼 더블클릭 중복 제출 방지: ref 가 즉시 가드, state 는 버튼 disabled 용
  const cropSubmittingRef = useRef(false);
  const [cropSubmitting, setCropSubmitting] = useState(false);
  const smartPoleSubmittingRef = useRef(false);
  const [smartPoleSubmitting, setSmartPoleSubmitting] = useState(false);
  const smartPoleEditSubmittingRef = useRef(false);
  const [smartPoleEditSubmitting, setSmartPoleEditSubmitting] = useState(false);
  const droneSubmittingRef = useRef(false);
  const [droneSubmitting, setDroneSubmitting] = useState(false);

  const submitCrop = async () => {
    if (cropSubmittingRef.current) return;
    if (!cropForm.nameKo.trim()) {
      setCropModalError("작물 한글명을 입력하세요.");
      return;
    }
    if (!cropForm.nameEn.trim()) {
      setCropModalError("작물 영문명을 입력하세요.");
      return;
    }
    setCropModalError("");
    cropSubmittingRef.current = true;
    setCropSubmitting(true);
    try {
      const payload = {
        nameKo: cropForm.nameKo.trim(),
        nameEn: cropForm.nameEn.trim(),
      };
      let createdCrop = null;
      if (cropEditingId) {
        await axios.put(`/api/admin/crops/${cropEditingId}`, payload);
      } else {
        const { data } = await axios.post("/api/admin/crops", payload);
        createdCrop = data?.data;
      }
      await loadCrops();
      if (cropEditingId) {
        // 작물명은 농장 테이블의 '작물' 컬럼에도 표시되므로 함께 갱신
        await loadFarms();
        closeCropModal();
      } else if (cropModalFromEditFlow) {
        const selectedCropId = createdCrop?.cropId ?? createdCrop?.id;
        if (selectedCropId != null) {
          setEditCropId(String(selectedCropId));
        }
        closeCropModal();
      } else {
        setCropForm(EMPTY_CROP_FORM);
      }
    } catch (err) {
      setCropModalError(err?.response?.data?.error?.message || err.message);
    } finally {
      cropSubmittingRef.current = false;
      setCropSubmitting(false);
    }
  };

  const deleteCrop = async (cropId) => {
    setCropError("");
    try {
      await axios.delete(`/api/admin/crops/${cropId}`);
      setCrops((prev) => prev.filter((c) => c.cropId !== cropId));
    } catch (err) {
      setCropError(err?.response?.data?.error?.message || err.message);
    }
  };

  const openSmartPoleModal = (options = {}) => {
    const { presetUserId, lockUser, presetDeviceType } = options;
    setSmartPoleModalError("");
    setSmartPoleForm({
      ...EMPTY_SMART_POLE_FORM,
      userId: presetUserId ? String(presetUserId) : "",
      // 명시 지정이 없으면 지금 보고 있는 탭이 곧 장치 유형이다.
      deviceType: normalizeDeviceType(
        presetDeviceType ?? (adminTab === "smartCubes" ? "CUBE" : "POLE")
      ),
    });
    setSmartPoleModalLockUser(Boolean(lockUser));
    if (!farms.length) loadFarms();
    if (!smartPoles.length) loadSmartPoles();
    setIsSmartPoleModalOpen(true);
  };

  const closeSmartPoleModal = () => {
    setIsSmartPoleModalOpen(false);
    setSmartPoleModalError("");
    setSmartPoleModalLockUser(false);
    setSmartPoleForm(EMPTY_SMART_POLE_FORM);
  };

  const submitSmartPole = async () => {
    if (smartPoleSubmittingRef.current) return;
    const name = smartPoleForm.name.trim();
    const serialNum = smartPoleForm.serialNum.trim();
    const modelName = smartPoleForm.modelName.trim();
    const locationName = smartPoleForm.locationName.trim();
    const latitude = Number(smartPoleForm.latitude);
    const longitude = Number(smartPoleForm.longitude);

    if (!smartPoleForm.userId) {
      setSmartPoleModalError("사용자를 선택하세요.");
      return;
    }
    if (!name) {
      setSmartPoleModalError("이름을 입력하세요.");
      return;
    }
    if (!serialNum) {
      setSmartPoleModalError("시리얼 번호를 입력하세요.");
      return;
    }
    if (!modelName) {
      setSmartPoleModalError("모델명을 입력하세요.");
      return;
    }
    if (Number.isNaN(latitude) || latitude < -90 || latitude > 90) {
      setSmartPoleModalError("위도는 -90 ~ 90 범위로 입력하세요.");
      return;
    }
    if (Number.isNaN(longitude) || longitude < -180 || longitude > 180) {
      setSmartPoleModalError("경도는 -180 ~ 180 범위로 입력하세요.");
      return;
    }
    if (!locationName) {
      setSmartPoleModalError("위치명을 입력하세요.");
      return;
    }
    smartPoleSubmittingRef.current = true;
    setSmartPoleSubmitting(true);
    try {
      const { data } = await axios.post("/api/admin/smartpoles", {
        name,
        serialNum,
        model: modelName,
        location: locationName,
        latitude,
        longitude,
        userId: Number(smartPoleForm.userId),
        ipAddress: null,
        status: "ACTIVE",
        deviceType: normalizeDeviceType(smartPoleForm.deviceType),
      });
      const created = data?.data;
      if (created) {
        await loadSmartPoles();
        if (smartPoleModalLockUser && created?.id) {
          // 라디오도 함께 맞춰야 방금 만든 장치와 화면 표시가 어긋나지 않는다.
          const createdType = normalizeDeviceType(smartPoleForm.deviceType);
          setEditDeviceType(createdType);
          if (createdType === "CUBE") {
            // 새 큐브는 체크된 채로 목록에 들어간다. 저장해야 농장에 붙는다.
            setEditCubeIds((prev) =>
              prev.includes(String(created.id)) ? prev : [...prev, String(created.id)]
            );
          } else {
            setEditSmartPoleId(String(created.id));
          }
        }
      }
      closeSmartPoleModal();
    } catch (err) {
      setSmartPoleModalError(
        err?.response?.data?.error?.message || err.message
      );
    } finally {
      smartPoleSubmittingRef.current = false;
      setSmartPoleSubmitting(false);
    }
  };

  const openSmartPoleDroneModal = (smartPoleId) => {
    const target = smartPoles.find((smartPole) => smartPole.id === smartPoleId);
    if (!target) return;

    setSmartPoleDroneTargetId(smartPoleId);
    setSmartPoleDroneId(resolveSmartPoleDroneId(target));
    setSmartPoleDroneModalError("");
    setIsSmartPoleDroneModalOpen(true);
  };

  const closeSmartPoleDroneModal = () => {
    setIsSmartPoleDroneModalOpen(false);
    setSmartPoleDroneTargetId(null);
    setSmartPoleDroneId("");
    setSmartPoleDroneModalError("");
  };

  const submitSmartPoleDroneSelection = async () => {
    if (!smartPoleDroneTargetId) return;
    if (!smartPoleDroneId) {
      setSmartPoleDroneModalError("드론을 선택하세요.");
      return;
    }

    const isNone = smartPoleDroneId === "__NONE__";
    const selectedDroneId = isNone ? null : Number(smartPoleDroneId);

    if (!isNone) {
      const selectedDrone = drones.find((drone) => drone.id === selectedDroneId);
      if (!selectedDrone) {
        setSmartPoleDroneModalError("유효한 드론을 선택하세요.");
        return;
      }

      const alreadyAssigned = smartPoles.find(
        (sp) => sp.droneId === selectedDroneId && sp.id !== smartPoleDroneTargetId
      );
      if (alreadyAssigned) {
        setSmartPoleDroneModalError("이미 다른 스마트폴에 할당된 드론입니다.");
        return;
      }
    }

    try {
      await axios.put(`/api/admin/smartpoles/${smartPoleDroneTargetId}`, {
        droneId: selectedDroneId,
      });
      await loadSmartPoles();
      closeSmartPoleDroneModal();
    } catch (err) {
      setSmartPoleDroneModalError(
        err?.response?.data?.error?.message || err.message
      );
    }
  };

  const deleteSmartPole = async (id) => {
    try {
      await axios.delete(`/api/admin/smartpoles/${id}`);
      await loadSmartPoles();
    } catch (err) {
      console.error("스마트폴 삭제 실패:", err);
    }
  };

  const openSmartPoleEditModal = (smartPoleId) => {
    const target = smartPoles.find((sp) => sp.id === smartPoleId);
    if (!target) return;
    setSmartPoleEditTargetId(smartPoleId);
    setSmartPoleEditForm({
      name: target.name || "",
      serialNum: target.serialNum || "",
      status: target.status || "ACTIVE",
    });
    setSmartPoleEditModalError("");
    setIsSmartPoleEditModalOpen(true);
  };

  const closeSmartPoleEditModal = () => {
    setIsSmartPoleEditModalOpen(false);
    setSmartPoleEditTargetId(null);
    setSmartPoleEditForm({ name: "", serialNum: "", status: "" });
    setSmartPoleEditModalError("");
  };

  // 농장은 여기서 보내지 않는다(BE 도 무시한다). 배정은 사용자 관리 › 수정 › 장비 연결에서만.
  const submitSmartPoleEdit = async () => {
    if (!smartPoleEditTargetId || smartPoleEditSubmittingRef.current) return;
    const name = smartPoleEditForm.name.trim();
    const serialNum = smartPoleEditForm.serialNum.trim();
    if (!name) { setSmartPoleEditModalError("이름을 입력하세요."); return; }
    if (!serialNum) { setSmartPoleEditModalError("시리얼 번호를 입력하세요."); return; }

    smartPoleEditSubmittingRef.current = true;
    setSmartPoleEditSubmitting(true);
    try {
      await axios.put(`/api/admin/smartpoles/${smartPoleEditTargetId}`, {
        name,
        serialNum,
      });
      await loadSmartPoles();
      closeSmartPoleEditModal();
    } catch (err) {
      setSmartPoleEditModalError(err?.response?.data?.error?.message || err.message);
    } finally {
      smartPoleEditSubmittingRef.current = false;
      setSmartPoleEditSubmitting(false);
    }
  };

  const openDroneModal = (options = {}) => {
    const { presetOwnerId, presetFarmId, lockOwner, fromUserFlow } = options;
    setDroneModalError("");
    setDroneEditingId(null);
    setDroneForm({
      ...EMPTY_DRONE_FORM,
      ownerId: presetOwnerId ? String(presetOwnerId) : "",
      farmId: presetFarmId ? String(presetFarmId) : "",
    });
    setDroneModalLockOwner(Boolean(lockOwner));
    setDroneModalFromUserFlow(Boolean(fromUserFlow));
    setIsDroneModalOpen(true);
  };

  const openDroneEditModal = async (droneId) => {
    setDroneModalError("");
    setDroneError("");
    try {
      const { data } = await axios.get(`/api/admin/drones/${droneId}`);
      const detail = data?.data;
      if (!detail?.id) {
        throw new Error("드론 정보를 불러오지 못했습니다.");
      }
      setDroneEditingId(detail.id);
      setDroneForm({
        ...EMPTY_DRONE_FORM,
        ownerId: detail.userId ? String(detail.userId) : "",
        farmId: detail.farmId ? String(detail.farmId) : "",
        droneName: detail.droneName ?? "",
        serialNum: detail.serialNum ?? "",
      });
      setDroneModalLockOwner(false);
      setDroneModalFromUserFlow(false);
      setIsDroneModalOpen(true);
    } catch (err) {
      setDroneError(err?.response?.data?.error?.message || err.message);
    }
  };

  const closeDroneModal = () => {
    setIsDroneModalOpen(false);
    setDroneModalError("");
    setDroneModalLockOwner(false);
    setDroneModalFromUserFlow(false);
    setDroneEditingId(null);
    setDroneForm(EMPTY_DRONE_FORM);
  };

  const submitDrone = async () => {
    if (droneSubmittingRef.current) return;
    if (!droneForm.ownerId) {
      setDroneModalError("사용자를 선택하세요.");
      return;
    }
    if (!droneForm.droneName.trim()) {
      setDroneModalError("드론 이름을 입력하세요.");
      return;
    }
    if (!droneForm.serialNum.trim()) {
      setDroneModalError("시리얼 번호를 입력하세요.");
      return;
    }
    setDroneModalError("");
    droneSubmittingRef.current = true;
    setDroneSubmitting(true);
    try {
      const droneName = droneForm.droneName.trim();
      const serialNum = droneForm.serialNum.trim();
      if (droneEditingId) {
        const payload = {
          userId: Number(droneForm.ownerId),
          droneName,
          serialNum,
        };
        await axios.put(`/api/admin/drones/${droneEditingId}`, payload);
        await loadDrones();
        await loadFarms();
        closeDroneModal();
      } else {
        const payload = {
          ownerId: Number(droneForm.ownerId),
          droneName,
          modelName: droneName,
          serialNum,
        };
        const { data } = await axios.post("/api/admin/drones", payload);
        const created = data?.data;
        await loadDrones();
        await loadFarms();
        if (droneModalLockOwner && created?.id) {
          setEditDroneId(String(created.id));
        }
        if (droneModalFromUserFlow && created?.id) {
          setFarmForm((prev) => ({ ...prev, droneId: String(created.id) }));
        }
        setDroneForm(EMPTY_DRONE_FORM);
        if (!droneModalFromUserFlow) {
          closeDroneModal();
        }
      }
    } catch (err) {
      setDroneModalError(err?.response?.data?.error?.message || err.message);
    } finally {
      droneSubmittingRef.current = false;
      setDroneSubmitting(false);
    }
  };

  const deleteDrone = async (droneId) => {
    setDroneError("");
    try {
      await axios.delete(`/api/admin/drones/${droneId}`);
      setDrones((prev) => prev.filter((d) => d.id !== droneId));
      await loadFarms();
    } catch (err) {
      setDroneError(err?.response?.data?.error?.message || err.message);
    }
  };

  const openEditModal = async (user) => {
    // 모달을 열 때마다 목록을 최신화한다. 드론 목록도 반드시 포함 —
    // 빠뜨리면 mount 시점의 낡은 drones state 로 "보유 드론 없음"이 잘못 뜬다.
    const [latestFarms, latestConnections, latestSmartPoles, latestCrops, latestDrones] = await Promise.all([
      loadFarms(),
      loadConnections(),
      loadSmartPoles(),
      loadCrops(),
      loadDrones(),
    ]);
    const farmSource = latestFarms ?? [];
    const connectionSource = latestConnections ?? connections;
    const smartPoleSource = latestSmartPoles ?? smartPoles;
    const cropSource = latestCrops ?? crops;
    const droneSource = latestDrones ?? drones;
    setEditUser(user);
    setEditForm({
      nickname: user.nickname ?? "",
      email: user.email ?? "",
      role: user.role ?? "USER",
    });
    const userFarm = farmSource.find((f) => String(f.ownerId) === String(user.id));
    applyEditFarmSelection(
      userFarm?.id,
      farmSource,
      connectionSource,
      smartPoleSource,
      user,
      cropSource,
      droneSource
    );
    setEditModalError("");
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditUser(null);
    setEditConnectionId(null);
    setEditDroneId("");
    setEditSmartPoleId("");
    // 다음에 여는 사용자에게 이전 선택이 새지 않도록 함께 초기화한다.
    setEditDeviceType("POLE");
    setEditCubeIds([]);
    setEditFogId("");
    setEditConnectedDroneOption(null);
    setEditConnectedSmartPoleOption(null);
    setEditModalError("");
    setEditSaving(false);
    editSavingRef.current = false;
  };

  const saveEditModal = async () => {
    if (!editUser || editSavingRef.current) return;
    // 서버도 같은 규칙으로 거부하지만, 저장 버튼을 눌러 보기 전에 알려준다.
    if (editFarmId && editDeviceType === "CUBE" && wirePreview.error) {
      setEditModalError(wirePreview.text);
      return;
    }
    editSavingRef.current = true;
    setEditSaving(true);
    setEditModalError("");
    try {
      const userPayload = {
        nickname: editForm.nickname?.trim() || null,
        email: editForm.email?.trim() || null,
        role: editForm.role,
      };
      const { data } = await axios.patch(
        `/api/admin/users/${editUser.id}`,
        userPayload
      );
      const updatedUser = data?.data;
      if (updatedUser) {
        setUsers((prev) =>
          prev.map((u) => (u.id === updatedUser.id ? updatedUser : u))
        );
      }

      if (editFarmId) {
        await axios.put(`/api/admin/farms/${editFarmId}`, {
          name: editFarmName.trim() || null,
          address: editFarmAddress.trim() || null,
          cropId: editCropId ? Number(editCropId) : null,
          userId: editUser.id,
        });

        // farm 허브 배선: 드론/에너지 장치를 이 농장의 최종 상태(desired state)로 지정.
        // deviceIds 가 비면 전부 해제. 유형을 바꿔 저장하면 이전 유형 장치는 서버가 모두 해제한다.
        await axios.put(`/api/admin/farms/${editFarmId}/devices`, {
          droneId: editDroneId ? Number(editDroneId) : null,
          deviceType: editDeviceType,
          deviceIds:
            editDeviceType === "CUBE"
              ? editCubeIds.map(Number)
              : editSmartPoleId
                ? [Number(editSmartPoleId)]
                : [],
          fogDeviceId: editDeviceType === "CUBE" && editFogId ? Number(editFogId) : null,
        });
      }

      await loadFarms();
      await loadDrones();
      await loadSmartPoles();
      await loadConnections();
      closeEditModal();
      setSaveSuccessOpen(true);
    } catch (err) {
      setEditModalError(err?.response?.data?.error?.message || err.message);
    } finally {
      editSavingRef.current = false;
      setEditSaving(false);
    }
  };

  return (
    <div className="admin-shell">
      <aside
        className="admin-sidebar"
      >
        <div className="admin-sidebar-header">
          <span>관리자 메뉴</span>
        </div>
        <nav className="admin-sidebar-nav">
          {[
            { key: "users", label: "사용자 관리", short: "U" },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              className={`admin-sidebar-item ${
                adminTab === item.key ? "is-active" : ""
              }`}
              onClick={() => setAdminTab(item.key)}
              data-short={item.short}
            >
              {item.label}
            </button>
          ))}
          <details
            open={["farms", "drones", "smartPoles", "smartCubes", "crops"].includes(adminTab)}
            style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}
          >
            <summary
              className="admin-sidebar-item"
              data-short="D"
              style={{
                alignItems: "center",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                listStyle: "none",
                width: "100%",
              }}
            >
              <span>{"세부 관리"}</span>
              <span className="admin-detail-arrow admin-detail-arrow-closed" aria-hidden="true">
                {"▼"}
              </span>
              <span className="admin-detail-arrow admin-detail-arrow-open" aria-hidden="true">
                {"▲"}
              </span>
            </summary>
            {[
              { key: "farms", label: "농장 관리", short: "F" },
              { key: "drones", label: "드론 관리", short: "D" },
              { key: "smartPoles", label: "스마트폴 관리", short: "S" },
              { key: "smartCubes", label: "태양광큐브 관리", short: "Q" },
              { key: "crops", label: "작물 관리", short: "C" },
            ].map((item) => (
              <button
                key={item.key}
                type="button"
                className={`admin-sidebar-item ${
                  adminTab === item.key ? "is-active" : ""
                }`}
                onClick={() => setAdminTab(item.key)}
                data-short={item.short}
                style={{ display: "block", marginLeft: 12, width: "calc(100% - 12px)" }}
              >
                <span aria-hidden="true">· </span>
                {item.label}
              </button>
            ))}
          </details>
        </nav>
        <div className="admin-sidebar-footer">
          <button
            type="button"
            className="admin-sidebar-item admin-sidebar-home"
            onClick={() => navigate("/")}
          >
            홈으로
          </button>
        </div>
      </aside>
      <div className="admin-content">
        {adminTab === "users" && (
          <div className="admin-users-page main">
      <div className="admin-users-header">
        <div className="admin-users-title">
          <h1>사용자 관리</h1>
          <p>계정 검색, 필터링, 권한 수정</p>
        </div>
        <div className="admin-users-header-actions">
          <button type="button" onClick={loadUsers} disabled={loading}>
            {loading ? "로딩 중..." : "새로고침"}
          </button>
          <button type="button" onClick={openCreateModal}>
            사용자 추가
          </button>
        </div>
      </div>

      <div className="admin-users-toolbar">
        <input
          type="text"
          placeholder="검색 (ID/아이디/닉네임/이메일/권한/상태)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
        />
        <CalmSelect
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="ALL">권한: 전체</option>
          <option value="USER">USER</option>
          <option value="ADMIN">ADMIN</option>
        </CalmSelect>
        <CalmSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="ALL">상태: 전체</option>
          <option value="ACTIVE">ACTIVE</option>
          <option value="SUSPENDED">SUSPENDED</option>
          <option value="DELETED">DELETED</option>
        </CalmSelect>
      </div>

      {error && <div className="admin-users-error">{error}</div>}

      <div className="admin-table">
          <div className="admin-table-head">
            <div
              className="admin-users-row admin-users-head"
              style={{ gridTemplateColumns: userGridTemplate }}
            >
              <div className="admin-header-cell">
                {renderSortLabel("ID", "id", "users", sortKey, sortDir)}
                <span
                  className="admin-col-resizer"
                  onMouseDown={startResize("users", 0)}
                />
              </div>
              <div className="admin-header-cell">
                {renderSortLabel(
                  "아이디",
                  "username",
                  "users",
                  sortKey,
                  sortDir
                )}
                <span
                  className="admin-col-resizer"
                  onMouseDown={startResize("users", 1)}
                />
              </div>
              <div className="admin-header-cell">
                {renderSortLabel(
                  "닉네임",
                  "nickname",
                  "users",
                  sortKey,
                  sortDir
                )}
                <span
                  className="admin-col-resizer"
                  onMouseDown={startResize("users", 2)}
                />
              </div>
              <div className="admin-header-cell">
                {renderSortLabel(
                  "이메일",
                  "email",
                  "users",
                  sortKey,
                  sortDir
                )}
                <span
                  className="admin-col-resizer"
                  onMouseDown={startResize("users", 3)}
                />
              </div>
              <div className="admin-header-cell">
                {renderSortLabel("권한", "role", "users", sortKey, sortDir)}
                <span
                  className="admin-col-resizer"
                  onMouseDown={startResize("users", 4)}
                />
              </div>
              <div className="admin-header-cell">
                {renderSortLabel(
                  "상태",
                  "accountStatus",
                  "users",
                  sortKey,
                  sortDir
                )}
                <span
                  className="admin-col-resizer"
                  onMouseDown={startResize("users", 5)}
                />
              </div>
              <div className="admin-header-cell">
                {renderSortLabel(
                  "마지막 로그인",
                  "lastLoginAt",
                  "users",
                  sortKey,
                  sortDir
                )}
                <span
                  className="admin-col-resizer"
                  onMouseDown={startResize("users", 6)}
                />
              </div>
              <div className="admin-header-cell">작업</div>
            </div>
          </div>

        <div className="admin-table-body">
          {filteredUsers.map((user) => {
            const isEditing = editingId === user.id;
            return (
              <div
                className="admin-users-row"
                key={user.id}
                style={{ gridTemplateColumns: userGridTemplate }}
              >
                <div>{user.id}</div>
                <div>{user.username}</div>
                <div>
                  {isEditing ? (
                    <input
                      value={draft.nickname}
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          nickname: e.target.value,
                        }))
                      }
                    />
                  ) : (
                    user.nickname
                  )}
                </div>
                <div>
                  {isEditing ? (
                    <input
                      value={draft.email}
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                    />
                  ) : (
                    user.email
                  )}
                </div>
                <div>
                  {isEditing ? (
                    <CalmSelect
                      value={draft.role}
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          role: e.target.value,
                        }))
                      }
                    >
                      <option value="USER">USER</option>
                      <option value="ADMIN">ADMIN</option>
                    </CalmSelect>
                  ) : (
                    <span
                      className={`admin-role-pill ${
                        user.role === "ADMIN" ? "is-admin" : ""
                      }`}
                    >
                      {user.role}
                    </span>
                  )}
                </div>
                <div>
                  <span
                    className={`admin-status ${
                      user.accountStatus === "ACTIVE"
                        ? "is-active"
                        : user.accountStatus === "SUSPENDED"
                        ? "is-suspended"
                        : ""
                    }`}
                  >
                    {user.accountStatus}
                  </span>
                </div>
                <div>{user.lastLoginAt || "-"}</div>
                <div className="admin-users-actions">
                  {isEditing ? (
                    <>
                      <button type="button" onClick={saveEdit}>
                        저장
                      </button>
                      <button type="button" onClick={cancelEdit}>
                        취소
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={() => openEditModal(user)}>
                        수정
                      </button>
                      <button type="button" onClick={() => setDeleteConfirm({ action: () => deleteUser(user.id) })}>
                        삭제
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {!users.length && !loading && (
            <div className="admin-users-empty">사용자가 없습니다.</div>
          )}
        </div>
      </div>
          </div>
        )}

        {adminTab === "crops" && (
          <div className="admin-users-page main">
            <div className="admin-users-header">
              <div className="admin-users-title">
                <h1>작물 관리</h1>
                <p>작물 목록 조회 및 등록</p>
              </div>
              <div className="admin-users-header-actions">
                <button type="button" onClick={loadCrops} disabled={cropLoading}>
                  {cropLoading ? "로딩 중..." : "새로고침"}
                </button>
                <button type="button" onClick={openCropModal}>
                  작물 등록
                </button>
              </div>
            </div>

            <div className="admin-users-toolbar">
              <input
                type="text"
                placeholder="검색 (코드/한글명/영문명)"
                value={cropQuery}
                onChange={(e) => setCropQuery(e.target.value)}
              />
            </div>

            {cropError && <div className="admin-users-error">{cropError}</div>}

            <div className="admin-table">
              <div className="admin-table-head">
                <div
                  className="admin-users-row admin-users-head admin-crop-head"
                  style={{ gridTemplateColumns: cropGridTemplate }}
                >
                  <div className="admin-header-cell">
                    {renderSortLabel(
                      "ID",
                      "cropId",
                      "crops",
                      cropSortKey,
                      cropSortDir
                    )}
                    <span
                      className="admin-col-resizer"
                      onMouseDown={startResize("crops", 0)}
                    />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel(
                      "한글명",
                      "nameKo",
                      "crops",
                      cropSortKey,
                      cropSortDir
                    )}
                    <span
                      className="admin-col-resizer"
                      onMouseDown={startResize("crops", 1)}
                    />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel(
                      "영문명",
                      "nameEn",
                      "crops",
                      cropSortKey,
                      cropSortDir
                    )}
                    <span
                      className="admin-col-resizer"
                      onMouseDown={startResize("crops", 2)}
                    />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel(
                      "상태",
                      "active",
                      "crops",
                      cropSortKey,
                      cropSortDir
                    )}
                    <span
                      className="admin-col-resizer"
                      onMouseDown={startResize("crops", 3)}
                    />
                  </div>
                  <div className="admin-header-cell" />
                </div>
              </div>

              <div className="admin-table-body">
                {filteredCrops.map((crop) => (
                  <div
                    className="admin-users-row admin-crop-row"
                    key={crop.cropId}
                    style={{ gridTemplateColumns: cropGridTemplate }}
                  >
                    <div>{crop.cropId}</div>
                    <div>{crop.nameKo}</div>
                    <div>{crop.nameEn}</div>
                    <div>
                      <span
                        className={`admin-badge ${
                          crop.active ? "active" : "inactive"
                        }`}
                      >
                        {crop.active ? "ACTIVE" : "INACTIVE"}
                      </span>
                    </div>
                    <div className="admin-users-actions">
                      <button type="button" onClick={() => openCropEditModal(crop)}>
                        수정
                      </button>
                      <button type="button" onClick={() => setDeleteConfirm({ action: () => deleteCrop(crop.cropId) })}>
                        삭제
                      </button>
                    </div>
                  </div>
                ))}

                {!filteredCrops.length && (
                  <div className="admin-users-empty">작물이 없습니다.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {adminTab === "drones" && (
          <div className="admin-users-page main">
            <div className="admin-users-header">
              <div className="admin-users-title">
                <h1>드론 관리</h1>
                <p>드론 목록 조회 및 등록</p>
              </div>
              <div className="admin-users-header-actions">
                <button type="button" onClick={loadDrones} disabled={droneLoading}>
                  {droneLoading ? "로딩 중..." : "새로고침"}
                </button>
                <button type="button" onClick={() => openDroneModal({})}>
                  드론 등록
                </button>
              </div>
            </div>

                <div className="admin-users-toolbar">
                  <input
                    type="text"
                    placeholder="검색 (ID/드론명/시리얼/닉네임)"
                    value={droneQuery}
                    onChange={(e) => setDroneQuery(e.target.value)}
                  />
                  <CalmSelect
                    value={droneStatusFilter}
                    onChange={(e) => setDroneStatusFilter(e.target.value)}
                  >
                    <option value="ALL">상태: 전체</option>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                    <option value="BROKEN">BROKEN</option>
                    <option value="RETIRED">RETIRED</option>
                    <option value="MAINTENANCE">MAINTENANCE</option>
                  </CalmSelect>
                </div>

            {droneError && <div className="admin-users-error">{droneError}</div>}

            <div className="admin-table">
              <div className="admin-table-head">
                <div
                  className="admin-users-row admin-users-head admin-drone-head"
                  style={{ gridTemplateColumns: droneGridTemplate }}
                >
                  <div className="admin-header-cell">
                    {renderSortLabel(
                      "ID",
                      "id",
                      "drones",
                      droneSortKey,
                      droneSortDir
                    )}
                    <span
                      className="admin-col-resizer"
                      onMouseDown={startResize("drones", 0)}
                    />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel(
                      "드론명",
                      "droneName",
                      "drones",
                      droneSortKey,
                      droneSortDir
                    )}
                    <span
                      className="admin-col-resizer"
                      onMouseDown={startResize("drones", 1)}
                    />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel(
                      "시리얼",
                      "serialNum",
                      "drones",
                      droneSortKey,
                      droneSortDir
                    )}
                    <span
                      className="admin-col-resizer"
                      onMouseDown={startResize("drones", 2)}
                    />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel(
                      "닉네임(ID)",
                      "userId",
                      "drones",
                      droneSortKey,
                      droneSortDir
                    )}
                    <span
                      className="admin-col-resizer"
                      onMouseDown={startResize("drones", 3)}
                    />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel(
                      "상태",
                      "droneStatus",
                      "drones",
                      droneSortKey,
                      droneSortDir
                    )}
                    <span
                      className="admin-col-resizer"
                      onMouseDown={startResize("drones", 4)}
                    />
                  </div>
                  <div className="admin-header-cell" />
                </div>
              </div>

              <div className="admin-table-body">
                {filteredDrones.map((drone) => (
                  <div
                    className="admin-users-row admin-drone-row"
                    key={drone.id}
                    style={{ gridTemplateColumns: droneGridTemplate }}
                  >
                    <div>{drone.id}</div>
                    <div>{drone.droneName || "-"}</div>
                    <div>{drone.serialNum}</div>
                    <div>{drone.userId != null ? `${drone.userNickname || "-"}(${drone.userId})` : "-"}</div>
                    <div>
                      {drone.droneStatus ? (
                        <span
                          className={`admin-status ${
                            drone.droneStatus === "ACTIVE"
                              ? "is-active"
                              : drone.droneStatus === "INACTIVE"
                              ? "is-suspended"
                              : ""
                          }`}
                        >
                          {drone.droneStatus}
                        </span>
                      ) : (
                        "-"
                      )}
                    </div>
                    <div className="admin-users-actions">
                      <button type="button" onClick={() => openDroneEditModal(drone.id)}>
                        수정
                      </button>
                      <button type="button" onClick={() => setDeleteConfirm({ action: () => deleteDrone(drone.id) })}>
                        삭제
                      </button>
                    </div>
                  </div>
                ))}

                {!filteredDrones.length && (
                  <div className="admin-users-empty">드론이 없습니다.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {adminTab === "farms" && (
          <div className="admin-users-page main">
            <div className="admin-users-header">
              <div className="admin-users-title">
                <h1>농장 관리</h1>
                <p>농장 목록 조회 및 등록</p>
              </div>
              <div className="admin-users-header-actions">
                <button type="button" onClick={loadFarms} disabled={farmLoading}>
                  {farmLoading ? "로딩 중..." : "새로고침"}
                </button>
                <button type="button" onClick={openFarmModal}>
                  농장 등록
                </button>
              </div>
            </div>

            <div className="admin-users-toolbar">
              <input
                type="text"
                placeholder="검색 (ID/농장명/주소/닉네임/작물)"
                value={farmQuery}
                onChange={(e) => setFarmQuery(e.target.value)}
              />
            </div>

            {farmError && <div className="admin-users-error">{farmError}</div>}

            <div className="admin-table">
              <div className="admin-table-head">
                <div
                  className="admin-users-row admin-users-head admin-farm-head"
                  style={{ gridTemplateColumns: farmGridTemplate }}
                >
                  <div className="admin-header-cell">
                    {renderSortLabel("ID", "id", "farms", farmSortKey, farmSortDir)}
                    <span className="admin-col-resizer" onMouseDown={startResize("farms", 0)} />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel("농장명", "name", "farms", farmSortKey, farmSortDir)}
                    <span className="admin-col-resizer" onMouseDown={startResize("farms", 1)} />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel("주소", "address", "farms", farmSortKey, farmSortDir)}
                    <span className="admin-col-resizer" onMouseDown={startResize("farms", 2)} />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel("닉네임(ID)", "ownerId", "farms", farmSortKey, farmSortDir)}
                    <span className="admin-col-resizer" onMouseDown={startResize("farms", 3)} />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel("작물", "cropName", "farms", farmSortKey, farmSortDir)}
                    <span className="admin-col-resizer" onMouseDown={startResize("farms", 4)} />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel("면적", "area", "farms", farmSortKey, farmSortDir)}
                    <span className="admin-col-resizer" onMouseDown={startResize("farms", 5)} />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel("연결 장치", "energyDeviceCount", "farms", farmSortKey, farmSortDir)}
                    <span className="admin-col-resizer" onMouseDown={startResize("farms", 6)} />
                  </div>
                  <div className="admin-header-cell">작업</div>
                </div>
              </div>

              <div className="admin-table-body">
                {filteredFarms.map((farm) => (
                  <div
                    className="admin-users-row admin-farm-row"
                    key={farm.id}
                    style={{ gridTemplateColumns: farmGridTemplate }}
                  >
                    <div>{farm.id}</div>
                    <div>{farm.name || "-"}</div>
                    <div>{farm.address || "-"}</div>
                    <div>
                      {farm.ownerId != null
                        ? `${farm.ownerNickname || "-"}(${farm.ownerId})`
                        : "-"}
                    </div>
                    <div>{farm.cropName || "-"}</div>
                    <div>{farm.area != null ? `${farm.area} m²` : "-"}</div>
                    <div>{formatFarmDevices(farm)}</div>
                    <div className="admin-users-actions">
                      <button type="button" onClick={() => openFarmEditModal(farm.id)}>
                        수정
                      </button>
                      <button type="button" onClick={() => setDeleteConfirm({ action: () => deleteFarm(farm.id) })}>
                        삭제
                      </button>
                    </div>
                  </div>
                ))}

                {!filteredFarms.length && (
                  <div className="admin-users-empty">농장이 없습니다.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 스마트폴 탭과 태양광큐브 탭은 화면 구성이 동일하고 device_type 만 다르다.
            블록을 복제하지 않고 deviceTab* 파생값으로 문구와 목록만 갈아끼운다. */}
        {(adminTab === "smartPoles" || adminTab === "smartCubes") && (
          <div className="admin-users-page main">
            <div className="admin-users-header">
              <div className="admin-users-title">
                <h1>{deviceTabLabel} 관리</h1>
                <p>{deviceTabLabel} 목록 조회 및 등록</p>
              </div>
              <div className="admin-users-header-actions">
                <button
                  type="button"
                  onClick={loadSmartPoles}
                  disabled={smartPoleLoading}
                >
                  {smartPoleLoading ? "로딩 중..." : "새로고침"}
                </button>
                <button
                  type="button"
                  onClick={() => openSmartPoleModal({ presetDeviceType: deviceTabType })}
                >
                  {deviceTabLabel} 등록
                </button>
              </div>
            </div>

            <div className="admin-users-toolbar">
              <input
                type="text"
                placeholder={`검색(ID/${deviceTabLabel}명/시리얼/닉네임)`}
                value={smartPoleQuery}
                onChange={(e) => setSmartPoleQuery(e.target.value)}
              />
              <CalmSelect
                value={smartPoleStatusFilter}
                onChange={(e) => setSmartPoleStatusFilter(e.target.value)}
              >
                <option value="ALL">상태: 전체</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
                <option value="MAINTENANCE">MAINTENANCE</option>
              </CalmSelect>
            </div>

            <div className="admin-table">
              <div className="admin-table-head">
                <div
                  className="admin-users-row admin-users-head admin-smartpole-head"
                  style={{ gridTemplateColumns: smartPoleGridTemplate }}
                >
                  <div className="admin-header-cell">
                    {renderSortLabel(
                      "ID",
                      "id",
                      "smartPoles",
                      smartPoleSortKey,
                      smartPoleSortDir
                    )}
                    <span
                      className="admin-col-resizer"
                      onMouseDown={startResize("smartPoles", 0)}
                    />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel(
                      `${deviceTabLabel}명`,
                      "name",
                      "smartPoles",
                      smartPoleSortKey,
                      smartPoleSortDir
                    )}
                    <span
                      className="admin-col-resizer"
                      onMouseDown={startResize("smartPoles", 1)}
                    />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel(
                      "시리얼 번호",
                      "serialNum",
                      "smartPoles",
                      smartPoleSortKey,
                      smartPoleSortDir
                    )}
                    <span
                      className="admin-col-resizer"
                      onMouseDown={startResize("smartPoles", 2)}
                    />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel(
                      "닉네임(ID)",
                      "userId",
                      "smartPoles",
                      smartPoleSortKey,
                      smartPoleSortDir
                    )}
                    <span
                      className="admin-col-resizer"
                      onMouseDown={startResize("smartPoles", 3)}
                    />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel(
                      "농장",
                      "farmName",
                      "smartPoles",
                      smartPoleSortKey,
                      smartPoleSortDir
                    )}
                    <span
                      className="admin-col-resizer"
                      onMouseDown={startResize("smartPoles", 4)}
                    />
                  </div>
                  {deviceTabType === "CUBE" && (
                    <div className="admin-header-cell">
                      {/* 관리자가 장비 연결에서 정한 역할. 장치가 보고한 역할(reportedRole)은 heartbeat 가 오면 붙인다. */}
                      {renderSortLabel(
                        "역할",
                        "nodeRole",
                        "smartPoles",
                        smartPoleSortKey,
                        smartPoleSortDir
                      )}
                      <span
                        className="admin-col-resizer"
                        onMouseDown={startResize("smartPoles", 5)}
                      />
                    </div>
                  )}
                  <div className="admin-header-cell">
                    {renderSortLabel(
                      "상태",
                      "status",
                      "smartPoles",
                      smartPoleSortKey,
                      smartPoleSortDir
                    )}
                    <span
                      className="admin-col-resizer"
                      onMouseDown={startResize("smartPoles", 6)}
                    />
                  </div>
                  <div className="admin-header-cell">작업</div>
                </div>
              </div>

              <div className="admin-table-body">
                {deviceTabRows.map((smartPole) => (
                  <div
                    className="admin-users-row admin-smartpole-row"
                    key={smartPole.id}
                    style={{ gridTemplateColumns: smartPoleGridTemplate }}
                  >
                    <div>{smartPole.id}</div>
                    <div>{smartPole.name || "-"}</div>
                    <div>{smartPole.serialNum}</div>
                    <div>{smartPole.userId != null ? `${smartPole.userNickname || "-"}(${smartPole.userId})` : "-"}</div>
                    <div>
                      {smartPole.farmId != null
                        ? `${smartPole.farmName || "-"}(${smartPole.farmId})`
                        : <span className="admin-dim">미배정</span>}
                    </div>
                    {deviceTabType === "CUBE" && (
                      <div>
                        {smartPole.farmId != null && smartPole.nodeRole ? (
                          <span className={`admin-role-badge ${String(smartPole.nodeRole).toLowerCase()}`}>
                            {smartPole.nodeRole}
                          </span>
                        ) : (
                          <span className="admin-dim">-</span>
                        )}
                      </div>
                    )}
                    <div>
                      {smartPole.status ? (
                        <span
                          className={`admin-status ${
                            smartPole.status === "ACTIVE"
                              ? "is-active"
                              : smartPole.status === "INACTIVE"
                              ? "is-suspended"
                              : ""
                          }`}
                        >
                          {smartPole.status}
                        </span>
                      ) : (
                        "-"
                      )}
                    </div>
                    <div className="admin-users-actions">
                      <button type="button" onClick={() => openSmartPoleEditModal(smartPole.id)}>
                        수정
                      </button>
                      <button type="button" onClick={() => setDeleteConfirm({ action: () => deleteSmartPole(smartPole.id) })}>
                        삭제
                      </button>
                    </div>
                  </div>
                ))}

                {!deviceTabRows.length && (
                  <div className="admin-users-empty">{deviceTabLabel}이 없습니다.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {adminTab === "connections" && (
          <div className="admin-users-page main">
            <div className="admin-users-header">
              <div className="admin-users-title">
                <h1>연결 관리</h1>
                <p>사용자·농장·드론·스마트폴 매칭 관리</p>
              </div>
              <div className="admin-users-header-actions">
                <button
                  type="button"
                  onClick={loadConnections}
                  disabled={connectionLoading}
                >
                  {connectionLoading ? "로딩 중..." : "새로고침"}
                </button>
                {/* <button type="button" onClick={openConnectionModal}>
                  연결 등록
                </button> */}
              </div>
            </div>

            <div className="admin-users-toolbar">
              <input
                type="text"
                placeholder="검색 (ID/닉네임/농장명/드론명/스마트폴명)"
                value={connectionQuery}
                onChange={(e) => setConnectionQuery(e.target.value)}
              />
            </div>

            <div className="admin-table">
              <div className="admin-table-head">
                <div
                  className="admin-users-row admin-users-head admin-connection-head"
                  style={{ gridTemplateColumns: connectionGridTemplate }}
                >
                  <div className="admin-header-cell">
                    {renderSortLabel("ID", "id", "connections", connectionSortKey, connectionSortDir)}
                    <span className="admin-col-resizer" onMouseDown={startResize("connections", 0)} />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel("닉네임(ID)", "userId", "connections", connectionSortKey, connectionSortDir)}
                    <span className="admin-col-resizer" onMouseDown={startResize("connections", 1)} />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel("농장명(ID)", "farmId", "connections", connectionSortKey, connectionSortDir)}
                    <span className="admin-col-resizer" onMouseDown={startResize("connections", 2)} />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel("드론명(ID)", "droneId", "connections", connectionSortKey, connectionSortDir)}
                    <span className="admin-col-resizer" onMouseDown={startResize("connections", 3)} />
                  </div>
                  <div className="admin-header-cell">
                    {renderSortLabel("스마트폴명(ID)", "smartPoleId", "connections", connectionSortKey, connectionSortDir)}
                    <span className="admin-col-resizer" onMouseDown={startResize("connections", 4)} />
                  </div>
                  <div className="admin-header-cell">작업</div>
                </div>
              </div>
              <div className="admin-table-body">
                {filteredConnections.map((conn) => (
                    <div
                      className="admin-users-row admin-connection-row"
                      key={conn.id}
                      style={{ gridTemplateColumns: connectionGridTemplate }}
                    >
                      <div>{conn.id}</div>
                      <div>{conn.userNickname ? `${conn.userNickname}(${conn.userId})` : "-"}</div>
                      <div>{conn.farmName ? `${conn.farmName}(${conn.farmId})` : "-"}</div>
                      <div>{conn.droneId != null ? `${conn.droneName || "-"}(${conn.droneId})` : "없음"}</div>
                      <div>{conn.smartPoleId != null ? `${conn.smartPoleName || "-"}(${conn.smartPoleId})` : "없음"}</div>
                      <div className="admin-users-actions">
                        <button type="button" onClick={() => openConnectionEditModal(conn.id)}>
                          수정
                        </button>
                        {/* <button type="button" onClick={() => setConnectionDeleteTarget(conn.id)}>
                          삭제
                        </button> */}
                      </div>
                    </div>
                ))}
                {!filteredConnections.length && (
                  <div className="admin-users-empty">연결 목록이 없습니다.</div>
                )}
              </div>
            </div>
          </div>
        )}

      {isModalOpen && (
        <div
          className="admin-modal-overlay"
          role="dialog"
          aria-modal="true"
          style={{ zIndex: isEditModalOpen ? 1200 : undefined }}
        >
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h2>
                {modalStep === 1 ? "사용자 정보 입력" : "농장 정보 및 경계 지정"}
              </h2>
              <button
                type="button"
                className="admin-modal-close"
                onClick={closeCreateModal}
                disabled={isSubmitting}
              >
                닫기
              </button>
            </div>

            {modalStep === 1 && (
              <div className="admin-modal-body">
                <div className="admin-modal-grid">
                  <div className="admin-input required">
                    <input
                      type="text"
                      placeholder="아이디"
                      className={userFieldErrors.username ? "is-invalid" : undefined}
                      title={userFieldErrors.username || undefined}
                      value={userForm.username}
                      onChange={(e) => {
                        clearUserFieldError("username");
                        setUserForm((prev) => ({
                          ...prev,
                          username: e.target.value,
                        }));
                      }}
                      autoComplete="off"
                    />
                    <span className="required-star">*</span>
                  </div>
                  <div className="admin-input required">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="비밀번호"
                      className={userFieldErrors.password ? "is-invalid" : undefined}
                      title={userFieldErrors.password || undefined}
                      value={userForm.password}
                      onChange={(e) => {
                        clearUserFieldError("password");
                        setUserForm((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }));
                      }}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="toggle-visibility"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? "숨김" : "보기"}
                    </button>
                    <span className="required-star">*</span>
                  </div>
                  <div className="admin-input required">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="비밀번호 확인"
                      className={userFieldErrors.confirmPassword ? "is-invalid" : undefined}
                      title={userFieldErrors.confirmPassword || undefined}
                      value={confirmPassword}
                      onChange={(e) => {
                        clearUserFieldError("confirmPassword");
                        setConfirmPassword(e.target.value);
                      }}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="toggle-visibility"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                    >
                      {showConfirmPassword ? "숨김" : "보기"}
                    </button>
                    <span className="required-star">*</span>
                  </div>
                  <div className="admin-input required">
                    <input
                      type="text"
                      placeholder="닉네임"
                      className={userFieldErrors.nickname ? "is-invalid" : undefined}
                      title={userFieldErrors.nickname || undefined}
                      value={userForm.nickname}
                      onChange={(e) => {
                        clearUserFieldError("nickname");
                        setUserForm((prev) => ({
                          ...prev,
                          nickname: e.target.value,
                        }));
                      }}
                    />
                    <span className="required-star">*</span>
                  </div>
                  <div className="admin-input required">
                    <input
                      type="email"
                      placeholder="이메일"
                      className={userFieldErrors.email ? "is-invalid" : undefined}
                      title={userFieldErrors.email || undefined}
                      value={userForm.email}
                      onChange={(e) => {
                        clearUserFieldError("email");
                        setUserForm((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }));
                      }}
                    />
                    <span className="required-star">*</span>
                  </div>
                  <div className="admin-input">
                    <input
                      type="text"
                      placeholder="전화번호(선택)"
                      className={userFieldErrors.phone ? "is-invalid" : undefined}
                      title={userFieldErrors.phone || undefined}
                      value={userForm.phone}
                      onChange={(e) => {
                        clearUserFieldError("phone");
                        setUserForm((prev) => ({
                          ...prev,
                          phone: e.target.value,
                        }));
                      }}
                    />
                  </div>
                  <div className="admin-input required">
                    <CalmSelect
                      value={userForm.role}
                      onChange={(e) =>
                        setUserForm((prev) => ({
                          ...prev,
                          role: e.target.value,
                        }))
                      }
                    >
                      <option value="USER">USER</option>
                      <option value="ADMIN">ADMIN</option>
                    </CalmSelect>
                    <span className="required-star">*</span>
                  </div>
                </div>
              </div>
            )}

            {modalStep === 2 && (
              <div className="admin-modal-body">
                <div className="admin-modal-grid">
                  <div className="admin-input required">
                    <input
                      type="text"
                      placeholder="농장 이름"
                      value={farmForm.name}
                      onChange={(e) =>
                        setFarmForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                    />
                    <span className="required-star">*</span>
                  </div>
                  <div className="admin-input required">
                    <input
                      type="text"
                      placeholder="주소"
                      value={farmForm.address}
                      onChange={(e) =>
                        setFarmForm((prev) => ({
                          ...prev,
                          address: e.target.value,
                        }))
                      }
                    />
                    <span className="required-star">*</span>
                  </div>
                  <div className="admin-input required admin-inline-field">
                    <CalmSelect
                      value={farmForm.cropId}
                      onChange={(e) =>
                        setFarmForm((prev) => ({
                          ...prev,
                          cropId: e.target.value,
                        }))
                      }
                    >
                      <option value="">작물 선택</option>
                      {crops.map((crop) => (
                        <option key={crop.cropId} value={crop.cropId}>
                          {crop.nameKo}
                        </option>
                      ))}
                    </CalmSelect>
                    <span className="required-star">*</span>
                    <button
                      type="button"
                      className="inline-action"
                      onClick={openCropModal}
                    >
                      작물 추가
                    </button>
                  </div>
                  <div className="admin-input admin-inline-field admin-map-search-field">
                    <input
                      type="text"
                      placeholder="장소 검색"
                      value={mapSearchQuery}
                      onChange={(e) => setMapSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleMapSearch();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="inline-action"
                      onClick={handleMapSearch}
                    >
                      검색
                    </button>
                  </div>
                </div>

                <div className="admin-map-panel">
                  <div className="admin-map-header">
                    <div className="admin-map-title">
                      경계 지정 (지도 클릭으로 점 추가)
                    </div>
                    <div className="admin-map-actions">
                      <button
                        type="button"
                        onClick={() =>
                          setBoundaryPoints((prev) => prev.slice(0, -1))
                        }
                        disabled={!boundaryPoints.length}
                      >
                        마지막 점 제거
                      </button>
                      <button
                        type="button"
                        onClick={() => setBoundaryPoints([])}
                        disabled={!boundaryPoints.length}
                      >
                        초기화
                      </button>
                    </div>
                  </div>
                  <div className="admin-map-container">
                    <APIProvider
                      apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                    >
                      <Map
                        defaultCenter={DEFAULT_CENTER}
                        defaultZoom={16}
                        mapId="tlatfarm-admin-map"
                        className="admin-google-map"
                        onClick={(e) => {
                          const latLng = e.detail?.latLng;
                          if (!latLng) return;
                          setBoundaryPoints((prev) => [
                            ...prev,
                            [latLng.lat, latLng.lng],
                          ]);
                        }}
                      >
                        {boundaryPoints.map((point, index) => (
                          <Marker
                            key={`${point[0]}-${point[1]}-${index}`}
                            position={{ lat: point[0], lng: point[1] }}
                          />
                        ))}
                        <MapPolygon points={boundaryPoints} />
                        <MapPanTo center={mapCenter} />
                      </Map>
                    </APIProvider>
                  </div>
                </div>
              </div>
            )}

            {modalError && <div className="admin-modal-error">{modalError}</div>}

            <div className="admin-modal-footer">
              {modalStep === 2 ? (
                <button type="button" onClick={() => setModalStep(1)}>
                  이전
                </button>
              ) : (
                <button type="button" onClick={closeCreateModal}>
                  취소
                </button>
              )}
              {modalStep === 1 ? (
                <button type="button" onClick={goToNextStep}>
                  다음
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submitUserAndFarm}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "처리 중..." : "생성"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 작물 모달은 사용자수정/농장등록/농장수정 모달 위에서 열리는 말단 모달인데,
          DOM 순서상 농장 모달들보다 먼저 렌더되어 같은 z-index 면 뒤에 깔린다
          (버튼이 죽은 것처럼 보임) — 항상 위로 고정 */}
      {isCropModalOpen && (
        <div
          className="admin-modal-overlay"
          role="dialog"
          aria-modal="true"
          style={{ zIndex: 1200 }}
        >
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h2>{cropEditingId ? "작물 수정" : "작물 등록"}</h2>
              <button
                type="button"
                className="admin-modal-close"
                onClick={closeCropModal}
              >
                닫기
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-modal-section-title">
                {cropEditingId ? "작물 정보 수정" : "새 작물 등록"}
              </div>
              <div className="admin-modal-grid">
                <div className="admin-input required">
                  <input
                    type="text"
                    placeholder="작물 한글명"
                    value={cropForm.nameKo}
                    onChange={(e) =>
                      setCropForm((prev) => ({
                        ...prev,
                        nameKo: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <input
                    type="text"
                    placeholder="작물 영문명"
                    value={cropForm.nameEn}
                    onChange={(e) =>
                      setCropForm((prev) => ({
                        ...prev,
                        nameEn: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
              </div>
            </div>

            {cropModalError && (
              <div className="admin-modal-error">{cropModalError}</div>
            )}

            <div className="admin-modal-footer">
              <button type="button" onClick={submitCrop} disabled={cropSubmitting}>
                {cropEditingId ? "저장" : "등록"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isSmartPoleModalOpen && (
        <div
          className="admin-modal-overlay"
          role="dialog"
          aria-modal="true"
          style={{ zIndex: isEditModalOpen ? 1200 : undefined }}
        >
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h2>{DEVICE_LABEL[normalizeDeviceType(smartPoleForm.deviceType)]} 등록</h2>
              <button
                type="button"
                className="admin-modal-close"
                onClick={closeSmartPoleModal}
              >
                닫기
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-modal-section-title">
                새 {DEVICE_LABEL[normalizeDeviceType(smartPoleForm.deviceType)]} 등록
              </div>
              <div className="admin-modal-grid">
                <div className="admin-input required">
                  <CalmSelect
                    value={smartPoleForm.userId ?? ""}
                    disabled={smartPoleModalLockUser}
                    onChange={(e) =>
                      setSmartPoleForm((prev) => ({
                        ...prev,
                        userId: e.target.value,
                      }))
                    }
                  >
                    <option value="">사용자 선택</option>
                    {activeUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.nickname || user.username}(ID:{user.id})
                      </option>
                    ))}
                  </CalmSelect>
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <input
                    type="text"
                    placeholder="이름"
                    value={smartPoleForm.name}
                    onChange={(e) =>
                      setSmartPoleForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <input
                    type="text"
                    placeholder="시리얼 번호"
                    value={smartPoleForm.serialNum}
                    onChange={(e) =>
                      setSmartPoleForm((prev) => ({
                        ...prev,
                        serialNum: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <input
                    type="text"
                    placeholder="모델명"
                    value={smartPoleForm.modelName}
                    onChange={(e) =>
                      setSmartPoleForm((prev) => ({
                        ...prev,
                        modelName: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <input
                    type="number"
                    step="any"
                    placeholder="위도"
                    value={smartPoleForm.latitude}
                    onChange={(e) =>
                      setSmartPoleForm((prev) => ({
                        ...prev,
                        latitude: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <input
                    type="number"
                    step="any"
                    placeholder="경도"
                    value={smartPoleForm.longitude}
                    onChange={(e) =>
                      setSmartPoleForm((prev) => ({
                        ...prev,
                        longitude: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <input
                    type="text"
                    placeholder="위치명"
                    value={smartPoleForm.locationName}
                    onChange={(e) =>
                      setSmartPoleForm((prev) => ({
                        ...prev,
                        locationName: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
              </div>
            </div>

            {smartPoleModalError && (
              <div className="admin-modal-error">{smartPoleModalError}</div>
            )}

            <div className="admin-modal-footer">
              <button type="button" onClick={submitSmartPole} disabled={smartPoleSubmitting}>
                등록
              </button>
            </div>
          </div>
        </div>
      )}

      {isSmartPoleDroneModalOpen && (
        <div className="admin-modal-overlay" role="dialog" aria-modal="true">
          <div className="admin-modal admin-drone-select-modal">
            <div className="admin-modal-header">
              <h2>드론 선택</h2>
              <button
                type="button"
                className="admin-modal-close"
                onClick={closeSmartPoleDroneModal}
              >
                닫기
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-modal-section-title">드론 ID(이름) 선택</div>
              <div className="admin-modal-grid admin-drone-select-grid">
                <div className="admin-input required">
                  <CalmSelect
                    value={smartPoleDroneId}
                    onChange={(e) => {
                      setSmartPoleDroneId(e.target.value);
                      setSmartPoleDroneModalError("");
                    }}
                    disabled={!drones.length}
                  >
                    <option value="">
                      {drones.length ? "드론 ID(이름)" : "해당 사용자의 드론 없음"}
                    </option>
                    <option value="__NONE__">드론 선택 없음</option>
                    {(() => {
                      const targetPole = smartPoles.find((sp) => sp.id === smartPoleDroneTargetId);
                      const targetUserId = targetPole?.userId;
                      const filtered = targetUserId
                        ? drones.filter((drone) => String(drone.userId) === String(targetUserId))
                        : drones;
                      return filtered.map((drone) => {
                        const assignedPole = smartPoles.find(
                          (sp) =>
                            sp.droneId === drone.id &&
                            sp.id !== smartPoleDroneTargetId
                        );
                        return (
                          <option
                            key={drone.id}
                            value={drone.id}
                            disabled={!!assignedPole}
                            style={assignedPole ? { backgroundColor: "#d0d0d0", color: "#888" } : {}}
                          >
                            {drone.id}({drone.droneName || "-"})
                            {assignedPole ? " (사용 중)" : ""}
                          </option>
                        );
                      });
                    })()}
                  </CalmSelect>
                  <span className="required-star">*</span>
                </div>
              </div>
            </div>

            {smartPoleDroneModalError && (
              <div className="admin-modal-error">{smartPoleDroneModalError}</div>
            )}

            <div className="admin-modal-footer">
              <button
                type="button"
                onClick={submitSmartPoleDroneSelection}
                disabled={!drones.length}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {isSmartPoleEditModalOpen && (
        <div className="admin-modal-overlay" role="dialog" aria-modal="true">
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h2>{DEVICE_LABEL[normalizeDeviceType(smartPoleEditTarget?.deviceType)]} 수정</h2>
              <button
                type="button"
                className="admin-modal-close"
                onClick={closeSmartPoleEditModal}
              >
                닫기
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-modal-section-title">
                {DEVICE_LABEL[normalizeDeviceType(smartPoleEditTarget?.deviceType)]} 정보 수정
              </div>
              <div className="admin-modal-grid">
                <div className="admin-input required">
                  <input
                    type="text"
                    placeholder="이름"
                    value={smartPoleEditForm.name}
                    onChange={(e) =>
                      setSmartPoleEditForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <input
                    type="text"
                    placeholder="시리얼 번호"
                    value={smartPoleEditForm.serialNum}
                    onChange={(e) =>
                      setSmartPoleEditForm((prev) => ({
                        ...prev,
                        serialNum: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
              </div>
              {/* 농장은 여기서 바꾸지 않는다. 배정은 사용자 관리 › 수정 › 장비 연결 한 곳에서만 —
                  예전엔 이 모달로 농장을 바꾸면 공존 검사 없이 저장돼 큐브 농장에 폴이 들어갈 수 있었다. */}
              <div className="admin-modal-note">
                현재 농장:{" "}
                {smartPoleEditTarget?.farmId != null
                  ? `${smartPoleEditTarget.farmName || "-"}(ID:${smartPoleEditTarget.farmId})`
                  : "미배정"}
                {" · "}농장 연결은 <b>사용자 관리 › 수정 › 장비 연결</b>에서 합니다.
              </div>
            </div>

            {smartPoleEditModalError && (
              <div className="admin-modal-error">{smartPoleEditModalError}</div>
            )}

            <div className="admin-modal-footer">
              <button type="button" onClick={submitSmartPoleEdit} disabled={smartPoleEditSubmitting}>
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {isDroneModalOpen && (
        <div
          className="admin-modal-overlay"
          role="dialog"
          aria-modal="true"
          style={{ zIndex: isEditModalOpen ? 1200 : undefined }}
        >
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h2>{droneEditingId ? "드론 수정" : "드론 등록"}</h2>
              <button
                type="button"
                className="admin-modal-close"
                onClick={closeDroneModal}
              >
                닫기
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-modal-section-title">
                {droneEditingId ? "드론 정보 수정" : "새 드론 등록"}
              </div>
              <div className="admin-modal-grid">
                <div className="admin-input required">
                  <CalmSelect
                    value={droneForm.ownerId}
                    onChange={(e) =>
                      setDroneForm((prev) => ({
                        ...prev,
                        ownerId: e.target.value,
                      }))
                    }
                    disabled={droneModalLockOwner}
                  >
                    <option value="">사용자 선택</option>
                    {activeUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.nickname || user.username}(ID:{user.id})
                      </option>
                    ))}
                  </CalmSelect>
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <input
                    type="text"
                    placeholder="드론 이름"
                    value={droneForm.droneName}
                    onChange={(e) =>
                      setDroneForm((prev) => ({
                        ...prev,
                        droneName: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <input
                    type="text"
                    placeholder="시리얼 번호"
                    value={droneForm.serialNum}
                    onChange={(e) =>
                      setDroneForm((prev) => ({
                        ...prev,
                        serialNum: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
              </div>
            </div>

            {droneModalError && (
              <div className="admin-modal-error">{droneModalError}</div>
            )}

            <div className="admin-modal-footer">
              <button type="button" onClick={submitDrone} disabled={droneSubmitting}>
                {droneEditingId ? "저장" : "등록"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && editUser && (
        <div className="admin-modal-overlay" role="dialog" aria-modal="true">
          {/* 높이 고정(CSS --user-edit): 큐브 목록이 붙어도 모달 크기가 안 바뀌고 본문만 스크롤한다. */}
          <div className="admin-modal admin-modal--user-edit">
            <div className="admin-modal-header">
              <h2>사용자 수정 및 연결</h2>
              <button
                type="button"
                className="admin-modal-close"
                onClick={closeEditModal}
                disabled={editSaving}
              >
                닫기
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-modal-section-title">사용자 정보</div>
              <div className="admin-modal-grid">
                <div className="admin-input required">
                  <input
                    type="text"
                    placeholder="닉네임"
                    value={editForm.nickname}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        nickname: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <input
                    type="email"
                    placeholder="이메일"
                    value={editForm.email}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <CalmSelect
                    value={editForm.role}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        role: e.target.value,
                      }))
                    }
                  >
                    <option value="USER">USER</option>
                    <option value="ADMIN">ADMIN</option>
                  </CalmSelect>
                  <span className="required-star">*</span>
                </div>
              </div>

              <div className="admin-modal-section-title">{"\uB18D\uC7A5 \uC120\uD0DD"}</div>
              <div className="admin-modal-grid">
                <div className="admin-input admin-inline-field">
                  <CalmSelect
                    value={editFarmId}
                    onChange={(e) => applyEditFarmSelection(e.target.value)}
                  >
                    <option value="">
                      {editUserFarms.length
                        ? "\uB18D\uC7A5 \uC120\uD0DD"
                        : "\uBCF4\uC720 \uB18D\uC7A5 \uC5C6\uC74C"}
                    </option>
                    {editUserFarms.map((farm) => {
                      const isCurrent = String(farm.id) === String(editFarmId);
                      const inUse = connectedFarmIds.has(farm.id) && !isCurrent;
                      return (
                        <option key={farm.id} value={farm.id}>
                          {farm.name || "-"} (ID:{farm.id})
                          {isCurrent
                            ? " - \uD604\uC7AC \uC5F0\uACB0\uB428"
                            : inUse
                              ? " - \uC5F0\uACB0\uB428"
                              : ""}
                        </option>
                      );
                    })}
                  </CalmSelect>
                  <button
                    type="button"
                    className="inline-action"
                    onClick={() => openFarmModal({ presetOwnerId: editUser.id })}
                  >
                    {"\uB18D\uC7A5 \uB4F1\uB85D"}
                  </button>
                </div>
              </div>

              {editFarmId ? (
                <>
                  <div className="admin-modal-section-title">농장 정보 수정</div>
                  <div className="admin-modal-grid">
                    <div className="admin-input">
                      <input
                        type="text"
                        placeholder="농장 이름"
                        value={editFarmName}
                        onChange={(e) => setEditFarmName(e.target.value)}
                      />
                    </div>
                    <div className="admin-input">
                      <input
                        type="text"
                        placeholder="주소"
                        value={editFarmAddress}
                        onChange={(e) => setEditFarmAddress(e.target.value)}
                      />
                    </div>
                    <div className="admin-input admin-inline-field">
                      <CalmSelect
                        value={editCropId}
                        onChange={(e) => setEditCropId(e.target.value)}
                      >
                        <option value="">작물 선택</option>
                        {crops.map((crop) => (
                          <option key={crop.cropId} value={crop.cropId}>
                            {crop.nameKo}
                          </option>
                        ))}
                      </CalmSelect>
                      <button
                        type="button"
                        className="inline-action"
                        onClick={() => openCropModal({ fromEditFlow: true })}
                      >
                        작물 등록
                      </button>
                  </div>
                </div>
                  {/* 장치 유형은 섹션 제목 줄에 작게 붙인다. 그리드에 자식으로 넣으면
                      2열 배치가 밀리고, 아래 장치 칸만 2단이 되어 좌우가 어긋난다. */}
                  <div className="admin-modal-section-head">
                    <span className="admin-modal-section-title">장비 연결</span>
                    <div
                      className="admin-device-type"
                      role="radiogroup"
                      aria-label="장치 유형"
                    >
                      {DEVICE_TYPES.map((type) => (
                        <label key={type} className="admin-device-type-option">
                          <input
                            type="radio"
                            name="editDeviceType"
                            value={type}
                            checked={editDeviceType === type}
                            onChange={() => changeEditDeviceType(type)}
                          />
                          <span>{DEVICE_LABEL[type]}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="admin-modal-grid">
                    <div className="admin-input admin-inline-field">
                      <CalmSelect
                        value={editDroneId}
                        onChange={(e) => setEditDroneId(e.target.value)}
                      >
                        {/* placeholder 가 해제 역할을 겸한다 — 연결된 상태에서 이걸 고르고
                            저장하면 devices PUT 이 droneId: null 로 배선을 푼다 */}
                        <option value="">
                          {editDroneId
                            ? "드론 연결 해제"
                            : editUserDrones.length
                              ? "현재 사용 드론 선택"
                              : "보유 드론 없음"}
                        </option>
                        {editUserDrones.map((drone) => {
                          const isCurrent = String(drone.id) === String(editDroneId);
                          const connectedFarmId = getConnectedFarmIdByDevice("droneId", drone.id);
                          const assignedOtherFarm =
                            editFarmId &&
                            drone.farmId != null &&
                            String(drone.farmId) !== String(editFarmId);
                          const connectedOtherFarm =
                            editFarmId &&
                            connectedFarmId != null &&
                            String(connectedFarmId) !== String(editFarmId);
                          const inUse =
                            !isCurrent &&
                            (hasConnectedId(connectedDroneIds, drone.id) ||
                              assignedOtherFarm ||
                              connectedOtherFarm);
                          return (
                            <option key={drone.id} value={drone.id} disabled={inUse}>
                              {drone.droneName || "-"} / {drone.serialNum || "-"} (ID:{drone.id})
                              {inUse ? " - 연결됨" : ""}
                            </option>
                          );
                        })}
                      </CalmSelect>
                      <button
                        type="button"
                        className="inline-action"
                        onClick={() =>
                          openDroneModal({
                            presetOwnerId: editUser.id,
                            presetFarmId: editFarmId,
                            lockOwner: true,
                          })
                        }
                      >
                        드론 등록
                      </button>
                    </div>
                    {editDeviceType === "CUBE" ? (
                      <>
                        {/* 등록 버튼은 유형 라디오 바로 아래, 드론 칸 옆 오른쪽 칸. 폴 드롭다운 안의 버튼과 같은 자리감. */}
                        <div className="admin-cube-actions">
                          <button
                            type="button"
                            className="inline-action"
                            onClick={() =>
                              openSmartPoleModal({
                                presetUserId: editUser.id,
                                lockUser: true,
                                presetDeviceType: editDeviceType,
                              })
                            }
                          >
                            {DEVICE_LABEL[editDeviceType]} 등록
                          </button>
                        </div>
                      <div className="admin-cube-assign">
                        {/* 큐브는 체크한 만큼 들어간다. fog 는 그중 하나(라디오), 없음이 기본.
                            저장하면 PUT /farms/{id}/devices 가 deviceIds / fogDeviceId 로 최종 상태를 맞춘다. */}
                        {editUserSmartPoles.length ? (
                          <div className="admin-cube-list" role="group" aria-label="태양광큐브 연결">
                            <div className="admin-cube-row admin-cube-head">
                              <span />
                              <span>태양광큐브 (보유 목록)</span>
                              <span>상태</span>
                              <span>fog</span>
                            </div>
                            {editUserSmartPoles.map((cube) => {
                              const id = String(cube.id);
                              const checked = editCubeIds.includes(id);
                              const assignedOtherFarm =
                                editFarmId &&
                                cube.farmId != null &&
                                String(cube.farmId) !== String(editFarmId);
                              const disabled = !checked && (assignedOtherFarm || cube.status !== "ACTIVE");
                              const inputId = `edit-cube-${id}`;
                              return (
                                <div
                                  key={cube.id}
                                  className={`admin-cube-row${disabled ? " is-disabled" : ""}`}
                                >
                                  <input
                                    id={inputId}
                                    type="checkbox"
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={() => toggleEditCube(id)}
                                  />
                                  <label htmlFor={inputId}>
                                    {cube.name || "-"}
                                    <small>{cube.serialNum || "-"} · ID {cube.id}</small>
                                    {assignedOtherFarm && (
                                      <small className="admin-cube-note">다른 농장에 연결됨</small>
                                    )}
                                  </label>
                                  <span className="admin-cube-status">{cube.status || "-"}</span>
                                  <span className="admin-cube-fog">
                                    <input
                                      type="radio"
                                      name="editFogId"
                                      value={id}
                                      checked={editFogId === id}
                                      disabled={!checked}
                                      onChange={() => setEditFogId(id)}
                                      aria-label={`${cube.name || cube.serialNum || id} fog 지정`}
                                    />
                                  </span>
                                </div>
                              );
                            })}
                            <div className="admin-cube-row admin-cube-nofog">
                              <input
                                id="edit-cube-nofog"
                                type="radio"
                                name="editFogId"
                                value=""
                                checked={!editFogId}
                                onChange={() => setEditFogId("")}
                              />
                              <label htmlFor="edit-cube-nofog">fog 없음 (전부 edge · 전력 공유 안 함)</label>
                            </div>
                          </div>
                        ) : (
                          <div className="admin-cube-empty">보유 태양광큐브 없음</div>
                        )}
                        <div className={`admin-wire-preview${wirePreview.error ? " is-error" : ""}`}>
                          {wirePreview.text}
                        </div>
                      </div>
                      </>
                    ) : (
                    <div className="admin-input admin-inline-field admin-inline-field--wide">
                      <CalmSelect
                        value={editSmartPoleId}
                        onChange={(e) => setEditSmartPoleId(e.target.value)}
                      >
                        <option value="">
                          {editSmartPoleId
                            ? `${DEVICE_LABEL[editDeviceType]} 연결 해제`
                            : editUserSmartPoles.length
                              ? `현재 사용 ${DEVICE_LABEL[editDeviceType]} 선택`
                              : `보유 ${DEVICE_LABEL[editDeviceType]} 없음`}
                        </option>
                        {editUserSmartPoles.map((smartPole) => {
                          const isCurrent =
                            String(smartPole.id) === String(editSmartPoleId);
                          const connectedFarmId = getConnectedFarmIdByDevice(
                            "smartPoleId",
                            smartPole.id
                          );
                          const assignedOtherFarm =
                            editFarmId &&
                            smartPole.farmId != null &&
                            String(smartPole.farmId) !== String(editFarmId);
                          const connectedOtherFarm =
                            editFarmId &&
                            connectedFarmId != null &&
                            String(connectedFarmId) !== String(editFarmId);
                          const inUse =
                            !isCurrent &&
                            (hasConnectedId(connectedSmartPoleIds, smartPole.id) ||
                              assignedOtherFarm ||
                              connectedOtherFarm);
                          return (
                            <option
                              key={smartPole.id}
                              value={smartPole.id}
                              disabled={inUse}
                            >
                              {smartPole.name || "-"} / {smartPole.serialNum || "-"} (ID:
                              {smartPole.id}){inUse ? " - 연결됨" : ""}
                            </option>
                          );
                        })}
                      </CalmSelect>
                      <button
                        type="button"
                        className="inline-action"
                        onClick={() =>
                          openSmartPoleModal({
                            presetUserId: editUser.id,
                            lockUser: true,
                            presetDeviceType: editDeviceType,
                          })
                        }
                      >
                        {DEVICE_LABEL[editDeviceType]} 등록
                      </button>
                    </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="admin-modal-section-title" style={{ color: "#888" }}>
                  연결된 농장이 없습니다.
                </div>
              )}
            </div>

            {editModalError && (
              <div className="admin-modal-error">{editModalError}</div>
            )}

            <div className="admin-modal-footer">
              <button type="button" onClick={saveEditModal} disabled={editSaving}>
                {editSaving ? "저장 중..." : "저장"}
              </button>
            </div>
          </div>
        </div>
      )}
      {saveSuccessOpen && (
        <div
          className="admin-modal-overlay"
          role="dialog"
          aria-modal="true"
          style={{ zIndex: 1600 }}
        >
          <div className="admin-modal" style={{ maxWidth: 320, textAlign: "center" }}>
            <div className="admin-modal-body">저장되었습니다.</div>
          </div>
        </div>
      )}
      {isFarmModalOpen && (
        <div className="admin-modal-overlay" role="dialog" aria-modal="true">
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h2>농장 정보 및 경계 지정</h2>
              <button
                type="button"
                className="admin-modal-close"
                onClick={closeFarmModal}
                disabled={farmModalSubmitting}
              >
                닫기
              </button>
            </div>

            <div className="admin-modal-body">
              <div className="admin-modal-grid">
                <div className="admin-input required">
                  <input
                    type="text"
                    placeholder="농장 이름"
                    value={farmModalForm.name}
                    onChange={(e) =>
                      setFarmModalForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <input
                    type="text"
                    placeholder="주소"
                    value={farmModalForm.address}
                    onChange={(e) =>
                      setFarmModalForm((prev) => ({
                        ...prev,
                        address: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <CalmSelect
                    value={farmModalForm.ownerId}
                    onChange={(e) =>
                      setFarmModalForm((prev) => ({
                        ...prev,
                        ownerId: e.target.value,
                      }))
                    }
                  >
                    <option value="">소유자 선택</option>
                    {activeUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.nickname || user.username}(ID:{user.id})
                      </option>
                    ))}
                  </CalmSelect>
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required admin-inline-field">
                  <CalmSelect
                    value={farmModalForm.cropId}
                    onChange={(e) =>
                      setFarmModalForm((prev) => ({
                        ...prev,
                        cropId: e.target.value,
                      }))
                    }
                  >
                    <option value="">작물 선택</option>
                    {crops.map((crop) => (
                      <option key={crop.cropId} value={crop.cropId}>
                        {crop.nameKo}
                      </option>
                    ))}
                  </CalmSelect>
                  <span className="required-star">*</span>
                  <button
                    type="button"
                    className="inline-action"
                    onClick={openCropModal}
                  >
                    작물 추가
                  </button>
                </div>
                <div className="admin-input admin-inline-field admin-map-search-field">
                  <input
                    type="text"
                    placeholder="장소 검색"
                    value={farmModalMapSearch}
                    onChange={(e) => setFarmModalMapSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleFarmModalMapSearch();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="inline-action"
                    onClick={handleFarmModalMapSearch}
                  >
                    검색
                  </button>
                </div>
              </div>

              <div className="admin-map-panel">
                <div className="admin-map-header">
                  <div className="admin-map-title">
                    경계 지정 (지도 클릭으로 점 추가)
                  </div>
                  <div className="admin-map-actions">
                    <button
                      type="button"
                      onClick={() =>
                        setFarmModalBoundary((prev) => prev.slice(0, -1))
                      }
                      disabled={!farmModalBoundary.length}
                    >
                      마지막 점 제거
                    </button>
                    <button
                      type="button"
                      onClick={() => setFarmModalBoundary([])}
                      disabled={!farmModalBoundary.length}
                    >
                      초기화
                    </button>
                  </div>
                </div>
                <div className="admin-map-container">
                  <APIProvider
                    apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                  >
                    <Map
                      defaultCenter={DEFAULT_CENTER}
                      defaultZoom={16}
                      mapId="tlatfarm-admin-farm-map"
                      className="admin-google-map"
                      onClick={(e) => {
                        const latLng = e.detail?.latLng;
                        if (!latLng) return;
                        setFarmModalBoundary((prev) => [
                          ...prev,
                          [latLng.lat, latLng.lng],
                        ]);
                      }}
                    >
                      {farmModalBoundary.map((point, index) => (
                        <Marker
                          key={`${point[0]}-${point[1]}-${index}`}
                          position={{ lat: point[0], lng: point[1] }}
                        />
                      ))}
                      <MapPolygon points={farmModalBoundary} />
                      <MapPanTo center={farmModalMapCenter} />
                    </Map>
                  </APIProvider>
                </div>
              </div>
            </div>

            {farmModalError && (
              <div className="admin-modal-error">{farmModalError}</div>
            )}

            <div className="admin-modal-footer">
              <button type="button" onClick={closeFarmModal}>
                취소
              </button>
              <button
                type="button"
                onClick={submitFarmModal}
                disabled={farmModalSubmitting}
              >
                {farmModalSubmitting ? "처리 중..." : "생성"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isFarmEditModalOpen && (
        <div className="admin-modal-overlay" role="dialog" aria-modal="true">
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h2>농장 수정</h2>
              <button
                type="button"
                className="admin-modal-close"
                onClick={closeFarmEditModal}
                disabled={farmEditSubmitting}
              >
                닫기
              </button>
            </div>

            <div className="admin-modal-body">
              <div className="admin-modal-grid">
                <div className="admin-input required">
                  <input
                    type="text"
                    placeholder="농장 이름"
                    value={farmEditForm.name}
                    onChange={(e) =>
                      setFarmEditForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <input
                    type="text"
                    placeholder="주소"
                    value={farmEditForm.address}
                    onChange={(e) =>
                      setFarmEditForm((prev) => ({
                        ...prev,
                        address: e.target.value,
                      }))
                    }
                  />
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <CalmSelect
                    value={farmEditForm.ownerId}
                    onChange={(e) =>
                      setFarmEditForm((prev) => ({
                        ...prev,
                        ownerId: e.target.value,
                      }))
                    }
                  >
                    <option value="">소유자 선택</option>
                    {activeUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.nickname || user.username}(ID:{user.id})
                      </option>
                    ))}
                  </CalmSelect>
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required admin-inline-field">
                  <CalmSelect
                    value={farmEditForm.cropId}
                    onChange={(e) =>
                      setFarmEditForm((prev) => ({
                        ...prev,
                        cropId: e.target.value,
                      }))
                    }
                  >
                    <option value="">작물 선택</option>
                    {crops.map((crop) => (
                      <option key={crop.cropId} value={crop.cropId}>
                        {crop.nameKo}
                      </option>
                    ))}
                  </CalmSelect>
                  <span className="required-star">*</span>
                  <button
                    type="button"
                    className="inline-action"
                    onClick={openCropModal}
                  >
                    작물 추가
                  </button>
                </div>
                <div className="admin-input admin-inline-field admin-map-search-field">
                  <input
                    type="text"
                    placeholder="장소 검색"
                    value={farmEditMapSearch}
                    onChange={(e) => setFarmEditMapSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleFarmEditMapSearch();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="inline-action"
                    onClick={handleFarmEditMapSearch}
                  >
                    검색
                  </button>
                </div>
              </div>

              <div className="admin-map-panel">
                <div className="admin-map-header">
                  <div className="admin-map-title">
                    경계 지정 (지도 클릭으로 점 추가)
                  </div>
                  <div className="admin-map-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setFarmEditBoundaryDirty(true);
                        setFarmEditBoundary((prev) => prev.slice(0, -1));
                      }}
                      disabled={!farmEditBoundary.length}
                    >
                      마지막 점 제거
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFarmEditBoundaryDirty(true);
                        setFarmEditBoundary([]);
                      }}
                      disabled={!farmEditBoundary.length}
                    >
                      초기화
                    </button>
                  </div>
                </div>
                <div className="admin-map-container">
                  <APIProvider
                    apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                  >
                    <Map
                      defaultCenter={DEFAULT_CENTER}
                      defaultZoom={16}
                      mapId="tlatfarm-admin-farm-edit-map"
                      className="admin-google-map"
                      onClick={(e) => {
                        const latLng = e.detail?.latLng;
                        if (!latLng) return;
                        setFarmEditBoundaryDirty(true);
                        setFarmEditBoundary((prev) => [
                          ...prev,
                          [latLng.lat, latLng.lng],
                        ]);
                      }}
                    >
                      {farmEditBoundary.map((point, index) => (
                        <Marker
                          key={`${point[0]}-${point[1]}-${index}`}
                          position={{ lat: point[0], lng: point[1] }}
                        />
                      ))}
                      <MapPolygon points={farmEditBoundary} />
                      <MapPanTo center={farmEditMapCenter} />
                    </Map>
                  </APIProvider>
                </div>
              </div>
            </div>

            {farmEditError && (
              <div className="admin-modal-error">{farmEditError}</div>
            )}

            <div className="admin-modal-footer">
              <button type="button" onClick={closeFarmEditModal}>
                취소
              </button>
              <button
                type="button"
                onClick={submitFarmEditModal}
                disabled={farmEditSubmitting}
              >
                {farmEditSubmitting ? "처리 중..." : "저장"}
              </button>
            </div>
          </div>
        </div>
      )}
      {connectionDeleteTarget != null && (
        <div className="admin-modal-overlay" onClick={() => setConnectionDeleteTarget(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="admin-modal-header">
              <h2>삭제 확인</h2>
              <button type="button" className="admin-modal-close" onClick={() => setConnectionDeleteTarget(null)}>✕</button>
            </div>
            <div className="admin-modal-body" style={{ textAlign: "center", padding: "24px 16px" }}>
              <p>정말 삭제하시겠습니까?</p>
            </div>
            <div className="admin-modal-footer" style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button type="button" onClick={() => setConnectionDeleteTarget(null)}>취소</button>
              <button type="button" onClick={() => deleteConnection(connectionDeleteTarget)} style={{ background: "#e74c3c", color: "#fff" }}>삭제</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm != null && (
        <div className="admin-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="admin-modal-header">
              <h2>삭제 확인</h2>
              <button type="button" className="admin-modal-close" onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div className="admin-modal-body" style={{ textAlign: "center", padding: "24px 16px" }}>
              <p>정말 삭제하시겠습니까?</p>
            </div>
            <div className="admin-modal-footer" style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button type="button" onClick={() => setDeleteConfirm(null)}>취소</button>
              <button type="button" onClick={() => { deleteConfirm.action(); setDeleteConfirm(null); }} style={{ background: "#e74c3c", color: "#fff" }}>삭제</button>
            </div>
          </div>
        </div>
      )}

      {isConnectionModalOpen && (
        <div className="admin-modal-overlay" role="dialog" aria-modal="true">
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h2>{connectionEditingIndex != null ? "연결 수정" : "연결 등록"}</h2>
              <button
                type="button"
                className="admin-modal-close"
                onClick={closeConnectionModal}
              >
                닫기
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-modal-section-title">
                {connectionEditingIndex != null ? "연결 정보 수정" : "새 연결 등록"}
              </div>
              <div className="admin-modal-grid">
                <div className="admin-input required">
                  <CalmSelect
                    value={connectionForm.userId}
                    onChange={(e) =>
                      setConnectionForm({
                        userId: e.target.value,
                        farmId: "",
                        droneId: "",
                        smartPoleId: "",
                      })
                    }
                  >
                    <option value="">사용자 선택</option>
                    {activeUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.nickname || user.username}(ID:{user.id})
                      </option>
                    ))}
                  </CalmSelect>
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <CalmSelect
                    value={connectionForm.farmId}
                    onChange={(e) =>
                      setConnectionForm((prev) => ({ ...prev, farmId: e.target.value }))
                    }
                    disabled={!connectionForm.userId}
                  >
                    <option value="">
                      {connectionForm.userId
                        ? connectionUserFarms.length
                          ? "농장 선택"
                          : "등록된 농장 없음"
                        : "사용자를 먼저 선택하세요"}
                    </option>
                    {connectionUserFarms.map((farm) => {
                      const inUse = connectedFarmIds.has(farm.id);
                      return (
                        <option key={farm.id} value={farm.id} disabled={inUse}>
                          {farm.name} (ID: {farm.id}){inUse ? " (연결됨)" : ""}
                        </option>
                      );
                    })}
                  </CalmSelect>
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <CalmSelect
                    value={connectionForm.droneId}
                    onChange={(e) =>
                      setConnectionForm((prev) => ({ ...prev, droneId: e.target.value }))
                    }
                    disabled={!connectionForm.userId}
                  >
                    <option value="">
                      {connectionForm.userId
                        ? connectionUserDrones.length
                          ? "드론 선택"
                          : "등록된 드론 없음"
                        : "사용자를 먼저 선택하세요"}
                    </option>
                    {connectionEditingIndex != null && (
                      <option value="__NONE__">드론 없음</option>
                    )}
                    {connectionUserDrones.map((drone) => {
                      const inUse = connectedDroneIds.has(drone.id);
                      return (
                        <option key={drone.id} value={drone.id} disabled={inUse}>
                          {drone.droneName} (ID: {drone.id}){inUse ? " (연결됨)" : ""}
                        </option>
                      );
                    })}
                  </CalmSelect>
                  <span className="required-star">*</span>
                </div>
                <div className="admin-input required">
                  <CalmSelect
                    value={connectionForm.smartPoleId}
                    onChange={(e) =>
                      setConnectionForm((prev) => ({ ...prev, smartPoleId: e.target.value }))
                    }
                    disabled={!connectionForm.userId}
                  >
                    <option value="">
                      {connectionForm.userId
                        ? connectionUserSmartPoles.length
                          ? "스마트폴 선택"
                          : "등록된 스마트폴 없음"
                        : "사용자를 먼저 선택하세요"}
                    </option>
                    {connectionEditingIndex != null && (
                      <option value="__NONE__">스마트폴 없음</option>
                    )}
                    {connectionUserSmartPoles.map((sp) => {
                      const inUse = connectedSmartPoleIds.has(sp.id);
                      return (
                        <option key={sp.id} value={sp.id} disabled={inUse}>
                          {sp.name} (ID: {sp.id}){inUse ? " (연결됨)" : ""}
                        </option>
                      );
                    })}
                  </CalmSelect>
                  <span className="required-star">*</span>
                </div>
              </div>
            </div>

            {connectionModalError && (
              <div className="admin-modal-error">{connectionModalError}</div>
            )}

            <div className="admin-modal-footer">
              <button type="button" onClick={submitConnection}>
                {connectionEditingIndex != null ? "저장" : "등록"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
  );
};

export default AdminTest;
